/**
 * Typed API client for the Alta FastAPI backend (port 5001).
 *
 * Behaviour:
 * - Sends the HttpOnly session cookie and its double-submit CSRF header.
 * - 401 → clear token, redirect to /login.
 * - 403 → silent (caller handles permission messaging).
 * - 4xx (other) / 5xx → emit a global error toast via useToast store.
 */
import axios, { type AxiosInstance, type AxiosError } from "axios";
import { unpackPerformanceWire } from "./performanceWire";
import { bindServerPeriods, serverPeriodsEnabled, serverPeriodCandidateRoot } from "./serverPeriods";
import { useToast } from "../hooks/useToast";
import { useAppStore } from "../hooks/useAppStore";
import { isTransientQueryError, queryRetryDelay } from "./queryRetry";
import { normalizeHnswIndexStatus, type HnswIndexStatus } from "./hnswIndexStatus";
export type { HnswIndexStatus } from "./hnswIndexStatus";
import {
  recordBenchmarkRequestAttempt,
  startBenchmarkRequestAttempt,
} from "./benchmarkPerformance";
import type {
  FertilityGroupDimension,
  FertilityGroupRow,
} from "./fertilityGroups";

const BASE_URL = import.meta.env.VITE_API_URL ?? "";
const DEFAULT_REQUEST_TIMEOUT_MS = 30_000;
export const SNAPSHOT_READ_TIMEOUT_MS = 90_000;
const READ_RECOVERY_BUDGET_MS = 8_000;

// One online gate per herd/root/page visit, shared by that visit's data blocks.
// No browser persistence. A navigation/session change invalidates the proof;
// long-lived visits recheck at most 60 seconds later. Never cache a rejection.
const SNAPSHOT_ACCESS_MAX_AGE_MS = 60_000;
const snapshotAccessChecks = new Map<string, { promise: Promise<unknown>; expires: number }>();
let snapshotAccessGeneration = 0;
let snapshotAccessVisit = "";
const sessionStatusReads = new Map<string, { promise: Promise<unknown>; expires: number }>();
const pageBootstrapReads = new Map<string, {
  promise: Promise<{ pointer: SnapshotCurrentResponse; group: HerdGroup | null }>;
  expires: number;
}>();
let statusReadGeneration = 0;
export function clearSnapshotAccessChecks() {
  snapshotAccessGeneration += 1;
  snapshotAccessChecks.clear();
  statusReadGeneration += 1;
  sessionStatusReads.clear();
  pageBootstrapReads.clear();
}
export function getSnapshotAccessVisit(): string {
  const route = typeof window === "undefined" ? "server"
    // Local controls normalize query parameters with replaceState while the
    // first pointer is in flight. That is not a new router visit.
    : JSON.stringify([window.location.pathname, window.history.state?.key]);
  if (route !== snapshotAccessVisit) {
    snapshotAccessVisit = route;
    snapshotAccessGeneration += 1;
    snapshotAccessChecks.clear();
    pageBootstrapReads.clear();
  }
  return `${snapshotAccessGeneration}:${route}`;
}
function snapshotAccessKey(root: string, herd: string) {
  return JSON.stringify([root, herd]);
}
/** Only non-authoritative status counters; auth/me and permissions never enter here. */
function shortSessionStatusRead<T>(key: string, load: () => Promise<T>): Promise<T> {
  const cached = sessionStatusReads.get(key);
  if (cached && cached.expires > Date.now()) return cached.promise as Promise<T>;
  const generation = statusReadGeneration;
  const promise = load().then(value => {
    if (generation !== statusReadGeneration) throw new axios.CanceledError("Session changed");
    return value;
  }).catch(error => {
    if (sessionStatusReads.get(key)?.promise === promise) sessionStatusReads.delete(key);
    throw error;
  });
  sessionStatusReads.set(key, { promise, expires: Date.now() + 2_000 });
  return promise;
}
async function checkSnapshotAccess(
  client: AxiosInstance, root: string, herds: string[],
) {
  const visit = getSnapshotAccessVisit();
  const responses = await Promise.all([...new Set(herds)].map((herd) => {
    const key = snapshotAccessKey(root, herd);
    let check = snapshotAccessChecks.get(key);
    if (!check || check.expires <= Date.now()) {
      const promise = client.get(`/api/snapshots/current/${encodeURIComponent(herd)}`, {
        params: { pinned_root: root }, skipErrorToast: true,
      }).catch(error => {
        if (snapshotAccessChecks.get(key)?.promise === promise) snapshotAccessChecks.delete(key);
        throw error;
      });
      check = { promise, expires: Date.now() + SNAPSHOT_ACCESS_MAX_AGE_MS };
      snapshotAccessChecks.set(key, check);
    }
    return check.promise;
  }));
  if (visit !== getSnapshotAccessVisit()) {
    throw new axios.CanceledError("Snapshot session changed during access check");
  }
  return (responses[0] as { data?: SnapshotCurrentResponse } | undefined)?.data;
}
export function authorizePinnedSnapshot(root: string, herds: string[]) {
  return checkSnapshotAccess(api, root, herds);
}

declare module "axios" {
  interface AxiosRequestConfig {
    /** Explicit semantic intent for read-only POST endpoints. */
    readRecovery?: boolean;
    skipAuthRedirect?: boolean;
    skipErrorToast?: boolean;
    _readStartedAt?: number;
    _readRetryCount?: number;
    _readRecoveryExhausted?: boolean;
    benchmarkNavigationId?: string;
    _benchmarkAttempt?: number;
    _benchmarkAttemptStartedAt?: number;
  }
}

/** Only reviewed read-only endpoints: never infer safety from POST alone. */
export function isApiRead(config: { method?: string; url?: string }): boolean {
  const method = (config.method ?? "get").toLowerCase();
  if (["get", "head", "options"].includes(method)) return true;
  return method === "post" && (
    /^\/api\/herds\/[^/]+\/(?:benchmark\/surface-query|benchmark-bands\/query)$/.test(config.url ?? "")
    || /^\/api\/snapshots\/[^/]+\/[^/]+\/benchmark\/(?:surface|bands)$/.test(config.url ?? "")
    || /^\/api\/herds\/[^/]+\/export\/(?:pdf|combined-pdf|summary-pdf)$/.test(config.url ?? "")
  );
}

function requestErrorKey(config: AxiosError["config"]): string {
  // No payload, headers or cookies in diagnostic/grouping identifiers.
  return `request-error:${(config?.method ?? "get").toUpperCase()}:${config?.url ?? ""}:${JSON.stringify(config?.params ?? null)}`;
}

/** Snapshot failures which are expected to be recoverable by the page retry. */
export const RETRYABLE_SNAPSHOT_CODES = new Set([
  "root_unavailable",
  "database_unavailable",
  "snapshot_database_unavailable",
  "snapshot_query_timeout",
  "snapshot_schema_unavailable",
  "snapshot_maintenance",
  "snapshot_conflict",
  "projection_unavailable",
  "restore_not_verified",
  "snapshot_unavailable",
  "snapshot_stale",
  "snapshot_not_ready",
  "snapshot_building",
  "snapshot_corrupt",
]);

/**
 * The URL is not a sufficient identity for a snapshot request: the same core
 * is requested for several roots, overlays, scopes and periods at once.
 * Keep this deliberately serialisable so it can also be put on Axios config.
 */
export interface SnapshotRequestIdentity {
  root?: string | null;
  overlay?: string | null;
  herd?: string | string[] | null;
  scope?: string | string[] | null;
  mode?: string | null;
  period?: string | null;
  requestedMonth?: string | null;
  referencePolicy?: "exact" | "latest_available_closed" | null;
  months?: readonly string[] | null;
  selections?: readonly {
    chart_id: string;
    kpi_key: string;
    mode: string;
    value: string | number | null;
  }[] | null;
  value?: string | number | null;
  axes?: {
    x_kpi: string;
    y_kpi: string;
    size_kpi: string;
  } | null;
  tableKpis?: readonly string[] | null;
}

function snapshotIdentityValue(identity: SnapshotRequestIdentity): string {
  const selections = identity.selections
    ? [...identity.selections]
      .map((selection) => ({
        chart_id: selection.chart_id,
        kpi_key: selection.kpi_key,
        mode: selection.mode,
        value: selection.value,
      }))
      .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)))
    : null;
  return JSON.stringify({
    root: identity.root ?? null,
    overlay: identity.overlay ?? null,
    herd: Array.isArray(identity.herd) ? [...identity.herd].map(String) : identity.herd ?? null,
    scope: Array.isArray(identity.scope) ? [...identity.scope].map(String) : identity.scope ?? null,
    mode: identity.mode ?? null,
    period: identity.period ?? null,
    months: identity.months ? [...identity.months] : null,
    selections,
    value: identity.value ?? null,
    axes: identity.axes ?? null,
    tableKpis: identity.tableKpis ? [...identity.tableKpis].sort() : null,
  });
}

export function snapshotToastKey(identity: SnapshotRequestIdentity | string): string {
  // Keep the string form for non-snapshot callers and old consumers. Snapshot
  // requests should always use the structured form above.
  return typeof identity === "string"
    ? `snapshot-technical:${identity.split("?")[0]}`
    : `snapshot-technical:${snapshotIdentityValue(identity)}`;
}

export interface ApiRequestOptions {
  /** Optional bound used by the Admin visit's shared recovery window. */
  timeout?: number;
  /** Keep expected background-refresh failures out of the global toast area. */
  skipErrorToast?: boolean;
  readRecovery?: boolean;
  /** Cancels obsolete page/herd work without treating it as a request failure. */
  signal?: AbortSignal;
  /** Full selection identity used for snapshot error grouping and clearing. */
  snapshotIdentity?: SnapshotRequestIdentity;
  /** Supply a generation from beginSnapshotRequest for externally coordinated calls. */
  snapshotGeneration?: number;
  /** Internal request family; defaults to the Axios URL path. */
  snapshotFamily?: string;
  /** Benchmark-only correlation context; never includes selection or farm data. */
  benchmarkNavigationId?: string;
}

interface ActiveSnapshotRequest {
  key: string;
  generation: number;
}

const activeSnapshotRequests = new Map<string, ActiveSnapshotRequest>();
let nextSnapshotGeneration = 0;

/** Starts a new generation and cleans up an obsolete selection in this family. */
export function beginSnapshotRequest(
  identity: SnapshotRequestIdentity,
  family = "default",
): number {
  const key = snapshotToastKey(identity);
  const previous = activeSnapshotRequests.get(family);
  if (previous?.key !== key && previous) {
    useToast.getState().removeByDedupeKey(previous.key);
  }
  const generation = ++nextSnapshotGeneration;
  activeSnapshotRequests.set(family, { key, generation });
  return generation;
}

/** Guards against an old response clearing or recreating a newer selection's toast. */
export function isCurrentSnapshotRequest(
  identity: SnapshotRequestIdentity,
  generation: number,
  family = "default",
): boolean {
  const active = activeSnapshotRequests.get(family);
  return active?.key === snapshotToastKey(identity) && active.generation === generation;
}

/** Clears a technical error only for the exact active response generation. */
export function clearSnapshotToastOnSuccess(
  identity: SnapshotRequestIdentity,
  generation: number,
  family = "default",
): boolean {
  if (!isCurrentSnapshotRequest(identity, generation, family)) return false;
  useToast.getState().removeByDedupeKey(snapshotToastKey(identity));
  return true;
}

function snapshotOptions(
  requestOptions: ApiRequestOptions | undefined,
  identity: SnapshotRequestIdentity,
  family?: string,
): ApiRequestOptions {
  return {
    ...requestOptions,
    snapshotIdentity: { ...identity, ...requestOptions?.snapshotIdentity },
    snapshotFamily: requestOptions?.snapshotFamily ?? family,
  };
}

export function httpErrorMessage(err: AxiosError): string {
  const status = err.response?.status;
  const isWrite = !isApiRead(err.config ?? {});
  // Never expose backend exception payloads or structured error details in a
  // toast.  Server failures use one stable, user-facing message so parallel
  // snapshot requests can also be deduplicated reliably.
  if (status != null && status >= 500) {
    return isWrite
      ? "Senden nicht möglich: Der Server konnte die Änderung nicht bestätigen. Bitte erneut versuchen."
      : "Daten konnten wegen einer vorübergehenden Serverstörung nicht geladen werden. Bitte die Seite erneut laden.";
  }
  const data = err.response?.data as { detail?: unknown } | undefined;
  if (data?.detail) {
    const d = data.detail;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) {
      const first = d[0] as { msg?: string } | undefined;
      return first?.msg ?? JSON.stringify(d);
    }
    return JSON.stringify(d);
  }
  if (!status) {
    return isWrite
      ? "Senden nicht möglich: Der Server ist nicht erreichbar. Die Änderung wurde nicht bestätigt. Bitte erneut versuchen."
      : "Daten konnten nicht geladen werden: Der Server ist nicht erreichbar. Bitte die Seite erneut laden.";
  }
  return `Anfragefehler (${status})`;
}

function benchmarkRequestId(headers: unknown): string | null {
  if (!headers || typeof headers !== "object") return null;
  const candidate = headers as { get?: (name: string) => unknown; [key: string]: unknown };
  const value = typeof candidate.get === "function"
    ? candidate.get("X-Request-ID")
    : Object.entries(candidate).find(([name]) => name.toLowerCase() === "x-request-id")?.[1];
  // The API generates a 12-byte base64url token (16 safe characters) for
  // each surface attempt. Never retain arbitrary proxy-supplied header data.
  return typeof value === "string" && /^[A-Za-z0-9_-]{16}$/.test(value) ? value : null;
}

function createClient(): AxiosInstance {
  // Global timeout: nach dem Aufwachen aus dem PWA-Hintergrund dürfen
  // eingeschlafene Verbindungen nie ewig hängen — axios bricht dann mit
  // ECONNABORTED ab (wird wie ein Netzwerkfehler behandelt).
  const client = axios.create({
    baseURL: BASE_URL,
    timeout: DEFAULT_REQUEST_TIMEOUT_MS,
    withCredentials: true,
  });

  client.interceptors.request.use(async (config) => {
    const match = /^\/api\/snapshots\/(multi\/)?([0-9a-f]{64})\/([^/]+)\//.exec(config.url ?? "");
    if (match && (config.method ?? "get").toLowerCase() === "get") {
      const identity = (config as ApiRequestOptions).snapshotIdentity;
      const selected = identity?.herd;
      const herds = match[1]
        ? (Array.isArray(selected) ? selected : config.params?.herd_ids)
        : [match[3]];
      if (!Array.isArray(herds) || herds.length === 0) {
        throw new Error("Snapshot access scope missing");
      }
      await checkSnapshotAccess(client, match[2], herds.map(String));
      // Read-side representation changes must not reuse an earlier immutable
      // browser body even if the source root itself has not changed.
      config.params = { ...config.params, representation: serverPeriodsEnabled ? "server-periods-v2" : "core-v2" };
    }
    if (serverPeriodsEnabled && (
      (config.url ?? "").startsWith("/api/snapshots/")
      || (config.url ?? "").includes("/export/combined-pdf")
    )) {
      config.params = { ...config.params, representation: "server-periods-v2" };
    }
    const benchmarkConfig = config as typeof config & {
      benchmarkNavigationId?: string;
      _benchmarkAttempt?: number;
      _benchmarkAttemptStartedAt?: number;
    };
    if (isApiRead(config) && config._readStartedAt == null) {
      config._readStartedAt = Date.now();
    }
    const requestConfig = config as typeof config & {
      snapshotIdentity?: SnapshotRequestIdentity;
      snapshotGeneration?: number;
      snapshotFamily?: string;
    };
    if (requestConfig.snapshotIdentity && !requestConfig.snapshotFamily) {
      requestConfig.snapshotFamily = String(requestConfig.url ?? "").split("?")[0];
    }
    if (requestConfig.snapshotIdentity && requestConfig.snapshotGeneration == null) {
      requestConfig.snapshotGeneration = beginSnapshotRequest(
        requestConfig.snapshotIdentity,
        requestConfig.snapshotFamily,
      );
    }
    // Snapshot reads validate the complete root/manifest/chunk closure and can
    // legitimately take longer on a cold Dev database. The old global 30 s
    // limit turned successful 38–40 s responses into false load errors.
    // Restrict the larger budget to identified read requests; writes and all
    // ordinary API traffic retain the global fail-fast timeout.
    const method = (config.method ?? "get").toLowerCase();
    if (
      requestConfig.snapshotIdentity
      && ["get", "head", "options", "post"].includes(method)
      && (config._readRetryCount ?? 0) === 0
    ) {
      config.timeout = SNAPSHOT_READ_TIMEOUT_MS;
    }
    // The session value is intentionally HttpOnly and cannot be read here.
    // The CSRF cookie is readable by design and must be echoed on writes.
    const csrf = document.cookie
      .split("; ")
      .find((entry) => entry.startsWith("alta_csrf="))
      ?.slice("alta_csrf=".length);
    if (csrf && !["get", "head", "options"].includes((config.method ?? "get").toLowerCase())) {
      config.headers["X-CSRF-Token"] = decodeURIComponent(csrf);
    }
    if (benchmarkConfig.benchmarkNavigationId) {
      // Already-aborted reads never reach the transport and are not HTTP
      // attempts. Clear retry metadata so a prior attempt cannot be repeated.
      benchmarkConfig._benchmarkAttempt = undefined;
      benchmarkConfig._benchmarkAttemptStartedAt = undefined;
      if (!config.signal?.aborted) {
        benchmarkConfig._benchmarkAttempt = startBenchmarkRequestAttempt(
          benchmarkConfig.benchmarkNavigationId,
        );
        benchmarkConfig._benchmarkAttemptStartedAt = performance.now();
        config.headers["X-Benchmark-Navigation-ID"] = benchmarkConfig.benchmarkNavigationId;
      }
    }
    return config;
  });

  client.interceptors.response.use(
    (res) => {
      res.data = unpackPerformanceWire(res.data);
      const benchmarkConfig = res.config as typeof res.config & {
        benchmarkNavigationId?: string;
        _benchmarkAttempt?: number;
        _benchmarkAttemptStartedAt?: number;
      };
      if (benchmarkConfig.benchmarkNavigationId && benchmarkConfig._benchmarkAttemptStartedAt != null) {
        recordBenchmarkRequestAttempt(
          benchmarkConfig.benchmarkNavigationId,
          benchmarkConfig._benchmarkAttempt ?? 0,
          performance.now() - benchmarkConfig._benchmarkAttemptStartedAt,
          "success",
          res.status,
          benchmarkRequestId(res.headers),
        );
      }
      // Reachability of one endpoint does not heal another failed query.
      if (isApiRead(res.config) && !(res.config as ApiRequestOptions).snapshotIdentity) {
        useToast.getState().removeByDedupeKey(requestErrorKey(res.config));
      }
      // A successful retry must close the technical snapshot toast created for
      // the same endpoint. Otherwise a resolved root can leave a stale alert
      // visible until its timer expires.
      const config = res.config as typeof res.config & {
        snapshotIdentity?: SnapshotRequestIdentity;
        snapshotGeneration?: number;
        snapshotFamily?: string;
      };
      if (
        config.snapshotIdentity
        && config.snapshotGeneration != null
      ) {
        clearSnapshotToastOnSuccess(
          config.snapshotIdentity,
          config.snapshotGeneration,
          config.snapshotFamily,
        );
      }
      return res;
    },
    async (err: AxiosError) => {
      const benchmarkConfig = err.config as (typeof err.config & {
        benchmarkNavigationId?: string;
        _benchmarkAttempt?: number;
        _benchmarkAttemptStartedAt?: number;
      });
      if (benchmarkConfig?.benchmarkNavigationId && benchmarkConfig._benchmarkAttemptStartedAt != null) {
        recordBenchmarkRequestAttempt(
          benchmarkConfig.benchmarkNavigationId,
          benchmarkConfig._benchmarkAttempt ?? 0,
          performance.now() - benchmarkConfig._benchmarkAttemptStartedAt,
          axios.isCancel(err) || err.code === "ERR_CANCELED"
            ? "cancelled"
            : err.response
              ? "http_error"
              : "network_error",
          err.response?.status ?? null,
          benchmarkRequestId(err.response?.headers),
        );
      }
      // React Query aborts queries on herd changes and unmount. A cancellation
      // is intentional: it must neither redirect/reset a herd nor show a toast.
      if (axios.isCancel(err) || err.code === "ERR_CANCELED") {
        return Promise.reject(err);
      }
      const status = err.response?.status;
      const recoveryConfig = err.config;
      if (recoveryConfig && isApiRead(recoveryConfig) && recoveryConfig.readRecovery !== false
        && isTransientQueryError(err)) {
        const attempts = recoveryConfig._readRetryCount ?? 0;
        const elapsed = Date.now() - (recoveryConfig._readStartedAt ?? Date.now());
        const delay = queryRetryDelay(attempts, err);
        if (attempts < 2 && elapsed + delay < READ_RECOVERY_BUDGET_MS) {
          recoveryConfig._readRetryCount = attempts + 1;
          await new Promise((resolve) => setTimeout(resolve, delay));
          if (recoveryConfig.signal?.aborted) return Promise.reject(new axios.CanceledError());
          // Measure the real wait: throttled tabs can resume well after backoff.
          const remaining = READ_RECOVERY_BUDGET_MS
            - (Date.now() - (recoveryConfig._readStartedAt ?? Date.now()));
          if (remaining > 0) {
            // Keep identity/generation, but never restore the initial cold-read
            // timeout when this request re-enters the request interceptor.
            recoveryConfig.timeout = Math.min(
              recoveryConfig.timeout ?? DEFAULT_REQUEST_TIMEOUT_MS,
              remaining,
            );
            return client.request(recoveryConfig);
          }
        }
        recoveryConfig._readRecoveryExhausted = true;
      }

      if (status === 401) {
        const url: string = (err.config as { url?: string })?.url ?? "";
        // Don't redirect when the login endpoint itself returns 401 (wrong
        // credentials) — let the login form show the error message instead.
        // Public activation pages also bootstrap by probing /auth/me without a
        // session; that expected 401 must not send the visitor away from the
        // demo or invitation they are about to redeem.
        const publicPath = /^\/(?:login|demo(?:\/|$)|invite(?:\/|$))/.test(
          window.location.pathname,
        );
        const publicBootstrapProbe = publicPath && url.includes("/auth/me");
        const devAuthBootstrapProbe = import.meta.env.DEV && url.includes("/auth/me");
        if (!err.config?.skipAuthRedirect && !url.includes("/auth/login") && !publicBootstrapProbe && !devAuthBootstrapProbe) {
          window.location.href = "/login";
        }
        return Promise.reject(err);
      }

      if (status === 403) {
        const url: string = (err.config as { url?: string })?.url ?? "";
        // Extract the herd_id from the URL (e.g. /api/herds/26622/kpis).
        const herdMatch = url.match(/\/api\/herds\/([^/]+)/);
        if (herdMatch) {
          const urlHerdId = herdMatch[1];
          const selectedId = useAppStore.getState().selectedHerdId;
          // Only reset herd selection when the currently selected herd is
          // the one that returned 403.  Satellite/companion sub-requests for
          // sibling herds in a multi-site group must NOT wipe the selection —
          // they are secondary calls, not access denials on the chosen herd.
          if (selectedId !== null && String(selectedId) === String(urlHerdId)) {
            useAppStore.getState().setHerd(null);
          }
        }
        return Promise.reject(err);
      }

      // Callers can opt out of the global toast by setting
      // `{ skipErrorToast: true }` in their axios request config.
      // Use this for background polling calls so a transient backend restart
      // (e.g. after a publish) doesn't flood the UI with error toasts.
        const config = err.config as ({
          skipErrorToast?: boolean;
          snapshotIdentity?: SnapshotRequestIdentity;
          snapshotGeneration?: number;
          snapshotFamily?: string;
        } | undefined);
        const skip = config
        ?.skipErrorToast;
      if (!skip) {
        const message = httpErrorMessage(err);
        const payload = err.response?.data as {
          code?: unknown;
          detail?: { code?: unknown };
        } | undefined;
        const rawSnapshotCode = payload?.detail?.code ?? payload?.code;
        const snapshotCode = typeof rawSnapshotCode === "string" ? rawSnapshotCode : null;
        const snapshotPath = String(err.config?.url ?? "");
        const snapshotIdentity = config?.snapshotIdentity;
        const retryableSnapshot = snapshotCode !== null
          && RETRYABLE_SNAPSHOT_CODES.has(snapshotCode)
          && snapshotPath.includes("/api/snapshots/");
        // A short backend/proxy interruption can fail all Overview queries
        // together. Group transport failures independently of the individual
        // endpoint so the user sees one useful message, not one toast per
        // parallel request (and retry).
        const currentGeneration = snapshotIdentity && config?.snapshotGeneration != null
          ? isCurrentSnapshotRequest(
            snapshotIdentity,
            config.snapshotGeneration,
            config.snapshotFamily,
          )
          : true;
        const dedupeKey = snapshotIdentity
          ? snapshotToastKey(snapshotIdentity)
          : retryableSnapshot
          ? snapshotToastKey(snapshotPath)
          : requestErrorKey(err.config);
        if (currentGeneration) useToast.getState().error(message, dedupeKey);
      }
      return Promise.reject(err);
    },
  );

  return client;
}

export const api = createClient();

export interface SnapshotMaintenanceStatus {
  active: boolean;
  maintenance_epoch: number;
  restore_epoch: number;
  started_at: string | null;
  last_activity_at: string;
  lease_expires_at: string | null;
  lease_owner: string | null;
  age_seconds: number | null;
  overdue: boolean;
  lease_expired: boolean;
}

export async function getSnapshotMaintenance(options?: ApiRequestOptions): Promise<SnapshotMaintenanceStatus> {
  const { data } = await api.get<SnapshotMaintenanceStatus>("/api/snapshots/admin/maintenance", options);
  return data;
}

export async function recoverSnapshotMaintenance(expectedEpoch: number): Promise<void> {
  await api.post("/api/snapshots/admin/maintenance/recover", { expected_epoch: expectedEpoch });
}

// ── Account data and privacy ────────────────────────────────────────────────

export interface DeletionRequest {
  id: number;
  user_id: number;
  request_type: "deletion";
  status: "received" | "in_review" | "completed" | "rejected";
  requested_at: string;
  reviewed_at: string | null;
  completed_at: string | null;
  admin_note: string | null;
}

export async function exportMyAccountData(): Promise<Blob> {
  const { data } = await api.get<Blob>("/api/account/data-export", {
    responseType: "blob",
  });
  return data;
}

export async function getMyDeletionRequest(): Promise<DeletionRequest | null> {
  const { data } = await api.get<DeletionRequest | null>("/api/account/deletion-request");
  return data;
}

export async function createMyDeletionRequest(): Promise<DeletionRequest> {
  const { data } = await api.post<DeletionRequest>("/api/account/deletion-request", {
    confirm: true,
  });
  return data;
}

// ── Auth ─────────────────────────────────────────────────────────────────────

export interface LoginResponse {
  user_id: number;
  username: string;
  full_name: string;
  role: "admin" | "user" | "berater" | "country_admin" | "kunde" | "demo" | "guest";
  must_change_password: boolean;
  is_country_admin?: boolean;
  country?: string | null;
  /** Username of the original admin when this session is an impersonation. */
  impersonator?: string | null;
  /** Mitarbeiter-Konto (per Einladung erstellt): erbt die Dashboard-Konfiguration des Hauptaccounts. */
  is_staff?: boolean;
  /** Locale selected by the creator of this signed demo link. */
  demo_language?: string | null;
  demo_expires_at?: string | null;
  slim_sync?: {
    animal_udder_rates: boolean;
    animal_inventory: boolean;
    dry_period_events: boolean;
    early_exit_details: boolean;
  };
}

// ── Einladungen (teilbare Links, task-1394) ─────────────────────────────────

export interface InvitationRow {
  id: number;
  invitee_name: string;
  invitee_email: string | null;
  target_role: "mitarbeiter" | "berater";
  created_by: string | null;
  herd_ids: string[];
  status: "pending" | "redeemed" | "expired" | "revoked";
  expires_at: string | null;
  redeemed_at: string | null;
  redeemed_username: string | null;
  revoked_at: string | null;
  created_at: string | null;
}

export interface AccessUserRow {
  id: number;
  username: string;
  full_name: string | null;
  role: string;
  is_active: boolean;
  is_staff: boolean;
  last_login_at: string | null;
  events_30d: number;
  herd_ids: string[];
  invited_by_me: boolean;
}

export async function createInvitation(body: {
  invitee_name: string;
  invitee_email?: string;
  target_role: "mitarbeiter" | "berater";
  herd_ids: string[];
  expires_in_days?: number;
}): Promise<{ id: number; invite_path: string; expires_at: string }> {
  const { data } = await api.post("/api/invitations", body);
  return data;
}

export async function getInvitations(): Promise<InvitationRow[]> {
  const { data } = await api.get<InvitationRow[]>("/api/invitations");
  return data;
}

export async function revokeInvitation(id: number): Promise<{ ok: boolean }> {
  const { data } = await api.post(`/api/invitations/${id}/revoke`);
  return data;
}

export async function renewInvitation(
  id: number,
): Promise<{ id: number; invite_path: string; expires_at: string }> {
  const { data } = await api.post(`/api/invitations/${id}/renew`);
  return data;
}

export async function getAccessUsers(): Promise<AccessUserRow[]> {
  const { data } = await api.get<AccessUserRow[]>("/api/invitations/access-users");
  return data;
}

export async function removeAccessUser(
  userId: number,
): Promise<{ ok: boolean; removed_herd_ids: string[]; deactivated: boolean }> {
  const { data } = await api.delete(`/api/invitations/access-users/${userId}`);
  return data;
}

export async function checkInvite(
  token: string,
): Promise<{ status: string; invitee_name?: string; target_role?: string }> {
  const { data } = await api.get(`/api/invite/${token}`, { skipErrorToast: true } as never);
  return data;
}

export async function redeemInvite(
  token: string,
  username: string,
  password: string,
): Promise<LoginResponse> {
  const { data } = await api.post<LoginResponse>(
    `/api/invite/${token}/redeem`,
    { username, password },
    { skipErrorToast: true } as never,
  );
  return data;
}

export async function impersonateUser(userId: number): Promise<LoginResponse> {
  const { data } = await api.post<LoginResponse>(`/api/admin/users/${userId}/impersonate`);
  return data;
}

export async function stopImpersonation(): Promise<void> {
  await api.post("/api/admin/impersonation/stop");
}
export async function login(username: string, password: string): Promise<LoginResponse> {
  const { data } = await api.post<LoginResponse>("/auth/login", { username, password }, {
    skipErrorToast: true, skipAuthRedirect: true,
  });
  return data;
}

export async function devLoginAvailable(): Promise<boolean> {
  const { data } = await api.get<{ enabled: boolean }>("/auth/dev-login/status", {
    skipErrorToast: true, skipAuthRedirect: true,
  });
  return data.enabled === true;
}

export async function devLogin(key: string): Promise<LoginResponse> {
  const { data } = await api.post<LoginResponse>("/auth/dev-login", { key }, { skipErrorToast: true, skipAuthRedirect: true });
  return data;
}

export async function logout(): Promise<void> {
  await api.post("/auth/logout", undefined, { skipErrorToast: true, skipAuthRedirect: true });
}
export async function exchangeClerkSession(session_token: string): Promise<LoginResponse> {
  const { data } = await api.post<LoginResponse>("/auth/clerk/exchange", { session_token }, {
    skipErrorToast: true, skipAuthRedirect: true,
  });
  return data;
}
export async function getMe(): Promise<LoginResponse> {
  // RequireAuth owns bootstrap/recovery UI. A transient startup failure here
  // must not also leave a global "server unreachable" toast behind.
  const { data } = await api.get<LoginResponse>("/auth/me", {
    skipErrorToast: true, skipAuthRedirect: true, readRecovery: false, timeout: 3_000,
  });
  return data;
}

export async function changePassword(
  old_password: string,
  new_password: string,
): Promise<{ ok: boolean }> {
  const { data } = await api.post<{ ok: boolean }>("/auth/change-password", {
    old_password,
    new_password,
  });
  return data;
}

export async function getAdminUserHerds(userId: number): Promise<{ user_id: number; herd_ids: string[] }> {
  const { data } = await api.get(`/api/admin/users/${userId}/herds`);
  return data;
}

export async function setAdminUserHerds(userId: number, herd_ids: string[]): Promise<{ ok: boolean }> {
  const { data } = await api.put(`/api/admin/users/${userId}/herds`, { herd_ids });
  return data;
}

export async function createAdminUser(body: {
  username: string;
  full_name?: string;
  role: string;
  password: string;
  email?: string;
  country?: string;
}): Promise<{ id: number; username: string }> {
  const { data } = await api.post<{ id: number; username: string }>("/api/admin/users", body);
  return data;
}

// ── Herds ─────────────────────────────────────────────────────────────────────

export interface Herd {
  herd_id: string;
  herd_name: string;
  region: string | null;
  size_class: string | null;
  milking_system: string | null;
  is_active: number;
  is_demo?: boolean | null;
  source_status?: "available" | "ddw_authorized_no_kpi_data" | "ddw_access_missing" | "intentionally_not_connected" | null;
}

export async function getHerds(requestOptions?: ApiRequestOptions): Promise<Herd[]> {
  const { data } = await api.get<Herd[]>("/api/herds", requestOptions as object);
  return data;
}

// ── Demo herd (Mustermann) — admin only ──────────────────────────────────────

export interface DemoHerdInfo {
  herd_id: string;
  herd_name: string;
  demo_source_herd_id: string | null;
  source_herd_name: string | null;
  kpi_rows: number;
  frozen_status?: { frozen: boolean; root_hash: string | null; installed_at: string | null };
}

export async function getDemoHerd(): Promise<DemoHerdInfo | null> {
  const { data } = await api.get<{ demo: DemoHerdInfo | null }>("/api/admin/demo-herd");
  return data.demo;
}

export async function createDemoHerd(sourceHerdId: string): Promise<{ ok: boolean }> {
  const { data } = await api.post<{ ok: boolean }>("/api/admin/demo-herd", {
    source_herd_id: sourceHerdId,
  });
  return data;
}

export interface DemoFrozenPreview {
  review_hash: string;
  source_root: string;
  report: {
    source_as_of: string | null;
    frozen: boolean;
    size_class: string;
    milking_system: string;
    peer_count: number;
    periods: Record<string, { first: string | null; last: string | null; months: number }>;
  };
}

export interface DemoPreviewJob {
  job_id: string;
  source_id?: string;
  status: string;
  progress?: string;
  error?: string;
  report?: DemoFrozenPreview & { elapsed_seconds?: number };
}

export async function getFrozenDemoPreview(jobId?: string | null, sourceId?: string) {
  const { data } = await api.get<DemoPreviewJob | null>("/api/admin/demo-herd/frozen-preview",
    { params: { job_id: jobId || undefined, source_id: sourceId || undefined } });
  return data;
}

export async function previewFrozenDemo(sourceHerdId: string): Promise<DemoPreviewJob> {
  const { data } = await api.post<DemoPreviewJob>("/api/admin/demo-herd/frozen-preview",
    { source_herd_id: sourceHerdId });
  return data;
}

export async function refreshFrozenDemo(sourceHerdId: string, reviewHash: string) {
  const { data } = await api.post<{ ok: boolean; root_hash: string }>("/api/admin/demo-herd/frozen-refresh",
    { source_herd_id: sourceHerdId, review_hash: reviewHash }, { timeout: 600_000 });
  return data;
}

export async function deleteDemoHerd(): Promise<{ ok: boolean }> {
  const { data } = await api.delete<{ ok: boolean }>("/api/admin/demo-herd");
  return data;
}

export interface DemoLinkInfo {
  id: number;
  token_prefix: string;
  label: string | null;
  expected_email: string;
  language: string;
  created_by_username: string;
  created_at: string;
  expires_at: string;
  redeemed_at: string | null;
  redeemed_by_email: string | null;
  revoked_at: string | null;
  last_seen_at: string | null;
  view_count: number;
  lead_count: number;
  status: "active" | "redeemed" | "expired" | "revoked";
}
export interface HerdGroupMember {
  herd_id: string;
  herd_name: string;
  location_type: string;
  location_type_raw: string;
  location_type_source: string;
  location_type_override: string | null;
  kpi_role: string;
  ai_suggestion: string | null;
  ai_confidence: number | null;
  ai_reasoning: string | null;
}

export interface HerdGroup {
  group_name: string;
  master_herd_id: string;
  members: HerdGroupMember[];
}

export async function getHerdGroup(
  herdId: string,
  requestOptions?: ApiRequestOptions,
): Promise<HerdGroup | null> {
  if (import.meta.env.VITE_PERF_PAGE_BOOTSTRAP === "true")
    return (await pageBootstrap(herdId, requestOptions)).group;
  const { data } = await api.get<HerdGroup | null>(`/api/herds/${herdId}/group`, requestOptions as object);
  return data;
}

export async function getAllHerdGroups(): Promise<HerdGroup[]> {
  const { data } = await api.get<HerdGroup[]>("/api/herds/groups/all");
  return data;
}

export async function analyzeHerdGroup(herdId: string): Promise<{ ok: boolean; message: string }> {
  const { data } = await api.post<{ ok: boolean; message: string }>(`/api/herds/${herdId}/group/analyze`);
  return data;
}

export async function patchHerdGroupMember(
  herdId: string,
  memberHerdId: string,
  body: { location_type_override?: string | null; kpi_role?: string }
): Promise<void> {
  await api.patch(`/api/herds/${herdId}/group/member/${memberHerdId}`, body);
}

// ── Overview ─────────────────────────────────────────────────────────────────

export type KpiDataStatus = "available" | "no_source" | "partial_source";

export interface OverviewResponse {
  herd: Herd;
  latest_kpis: Record<string, unknown> | null;
  data_status?: KpiDataStatus;
  coverage_version?: string | null;
  coverage_hash?: string | null;
  _overlay?: AnalyseResponse["_overlay"];
  benchmark_surface?: BenchmarkSurfaceResponse;
}

/** Immutable publication pointer. It is deliberately resolved before reading a core. */
export interface SnapshotCurrentResponse {
  /** A manually selected, anonymized edition, independent of live roots. */
  demo_frozen?: boolean;
  /** No frozen edition installed: use the authorized legacy demo endpoints. */
  demo_legacy?: boolean;
  source_as_of?: string | null;
  root_id: string;
  root_hash: string;
  generation: number;
  overlay_bundle_revision: {
    revision: number;
    overlay_hash: string;
  };
}

export interface SnapshotManifest {
  version: number;
  herd_id: string;
  chunks: Record<"overview" | "analyse" | "benchmark", string>;
}

/**
 * Return the published root authorized for this herd. Callers must pin this
 * value in memory and use it in every subsequent snapshot-core request.
 */
export async function getCurrentSnapshot(
  herdId: string,
  requestOptions?: ApiRequestOptions,
  forceFresh = false,
): Promise<SnapshotCurrentResponse> {
  const visit = getSnapshotAccessVisit();
  const quietOptions = {
    skipErrorToast: requestOptions?.skipErrorToast ?? true,
    ...requestOptions,
  };
  const { data } = !forceFresh && import.meta.env.VITE_PERF_PAGE_BOOTSTRAP === "true"
    ? { data: (await pageBootstrap(herdId, quietOptions)).pointer }
    : await api.get<SnapshotCurrentResponse>(
    `/api/snapshots/current/${herdId}`,
    {
      ...snapshotOptions(quietOptions, { herd: herdId, mode: "current" }, "snapshot-current"),
      ...(serverPeriodCandidateRoot ? { params: { preview_root: serverPeriodCandidateRoot } } : {}),
    } as object,
  );
  if (visit !== getSnapshotAccessVisit()) {
    throw new axios.CanceledError("Snapshot session changed during pointer read");
  }
  // This no-store response has already checked the returned root and herd.
  // Do not immediately request exactly the same proof again for each core.
  snapshotAccessChecks.set(snapshotAccessKey(data.root_hash, herdId), {
    promise: Promise.resolve({ data }), expires: Date.now() + SNAPSHOT_ACCESS_MAX_AGE_MS,
  });
  return data;
}

function pageBootstrap(herdId: string, requestOptions?: ApiRequestOptions) {
  const visit = getSnapshotAccessVisit();
  const key = `${visit}:${herdId}`;
  const cached = pageBootstrapReads.get(key);
  if (cached && cached.expires > Date.now()) return cached.promise;
  // Do not share caller-owned abort signals between independently mounted
  // consumers. Session/navigation checks still fence the eventual result.
  const { signal: _signal, ...options } = requestOptions ?? {};
  const promise = api.get<{ pointer: SnapshotCurrentResponse; group: HerdGroup | null }>(
    `/api/snapshots/bootstrap/${encodeURIComponent(herdId)}`,
    {
      ...options,
      ...(serverPeriodCandidateRoot ? { params: { preview_root: serverPeriodCandidateRoot } } : {}),
    } as object,
  ).then(({ data }) => {
    if (visit !== getSnapshotAccessVisit())
      throw new axios.CanceledError("Snapshot visit changed during bootstrap");
    return data;
  }).catch(error => {
    if (pageBootstrapReads.get(key)?.promise === promise) pageBootstrapReads.delete(key);
    throw error;
  });
  pageBootstrapReads.set(key, { promise, expires: Date.now() + SNAPSHOT_ACCESS_MAX_AGE_MS });
  return promise;
}

export async function getSnapshotManifest(
  rootHash: string,
  herdId: string,
  requestOptions?: ApiRequestOptions,
): Promise<SnapshotManifest> {
  const { data } = await api.get<SnapshotManifest>(
    `/api/snapshots/${rootHash}/${herdId}/manifest`,
    snapshotOptions(
      requestOptions,
      { root: rootHash, herd: herdId },
      "snapshot-manifest",
    ) as object,
  );
  return data;
}

export async function getSnapshotCore<T>(
  rootHash: string,
  herdId: string,
  core: "overview" | "analyse",
  requestOptions?: ApiRequestOptions,
): Promise<T> {
  const { data } = await api.get<T>(
    `/api/snapshots/${rootHash}/${herdId}/core/${core}`,
    snapshotOptions(
      requestOptions,
      { root: rootHash, herd: herdId, mode: core },
      `snapshot-core-${core}`,
    ) as object,
  );
  return bindServerPeriods(data);
}

export interface SnapshotMultiCore<T> {
  /** Deterministically aggregated data for the requested, ordered herd scope. */
  data: T;
  /** Individually pinned cores used for companion visualisations. */
  members: Record<string, T>;
}

export async function getSnapshotMultiCore<T>(
  rootHash: string,
  herdIds: string[],
  core: "overview" | "analyse",
  overlayHash?: string,
  requestOptions?: ApiRequestOptions,
): Promise<SnapshotMultiCore<T>> {
  const { data } = await api.get<SnapshotMultiCore<T>>(
    `/api/snapshots/multi/${rootHash}/core/${core}`,
    {
      params: {
        herd_ids: herdIds, overlay: overlayHash,
        ...(import.meta.env.VITE_PERF_SNAPSHOT_WIRE === "true" ? { wire: "dictionary-v1" } : {}),
      },
      paramsSerializer: { indexes: null },
      ...snapshotOptions(requestOptions, {
        root: rootHash,
        overlay: overlayHash,
        herd: herdIds,
        scope: herdIds,
        mode: core,
      }, `snapshot-multi-core-${core}`),
    } as object,
  );
  return {
    data: bindServerPeriods(data.data),
    members: Object.fromEntries(Object.entries(data.members).map(
      ([id, member]) => [id, bindServerPeriods(member)],
    )),
  };
}

export async function getSnapshotOverview(
  rootHash: string,
  herdId: string,
  requestOptions?: ApiRequestOptions,
): Promise<OverviewResponse> {
  return getSnapshotCore<OverviewResponse>(rootHash, herdId, "overview", requestOptions);
}

export async function getSnapshotMultiOverview(
  rootHash: string,
  herdIds: string[],
  overlayHash?: string,
  requestOptions?: ApiRequestOptions,
): Promise<SnapshotMultiCore<OverviewResponse>> {
  return getSnapshotMultiCore<OverviewResponse>(
    rootHash, herdIds, "overview", overlayHash, requestOptions,
  );
}

export async function getHerdOverview(
  herdId: string,
  params?: { herd_ids?: string },
  requestOptions?: ApiRequestOptions,
): Promise<OverviewResponse> {
  const { data } = await api.get<OverviewResponse>(
    `/api/herds/${herdId}/overview`,
    { params, ...requestOptions } as object,
  );
  return data;
}

// ── KPIs ─────────────────────────────────────────────────────────────────────

export async function getKpiTimeseries(
  herdId: string,
  params?: { months?: string; from_month?: string; to_month?: string; herd_ids?: string },
  requestOptions?: ApiRequestOptions,
): Promise<Record<string, unknown>[]> {
  const { data } = await api.get(
    `/api/herds/${herdId}/kpis`,
    { params, ...requestOptions } as object,
  );
  return data;
}

export type BenchmarkSelectionMode = "all" | "size_class" | "peer_group" | "milking_system";
export interface BenchmarkResponse {
  herd_id: string;
  month: string | null;
  peer_group: string;
  size_filter?: string | null;
  milking_system?: string | null;
  herd_rows: Record<string, unknown>[];
  peer_rows: Record<string, unknown>[];
  peer_count: number;
}

export async function getHerdBenchmark(
  herdId: string,
  params?: {
    month?: string;
    peer_group_id?: number;
    size_filter?: string;
    milking_system?: string;
  },
  requestOptions?: ApiRequestOptions,
): Promise<BenchmarkResponse> {
  const { data } = await api.get<BenchmarkResponse>(
    `/api/herds/${herdId}/benchmark`,
    { params, ...requestOptions } as object,
  );
  return data;
}

export interface PeerGroup {
  id: number;
  name: string;
  herd_count: number;
  created_at: string;
  created_by?: string | null;
}

export async function getPeerGroups(): Promise<PeerGroup[]> {
  const { data } = await api.get<PeerGroup[]>("/api/benchmark/peer-groups");
  return data;
}

export async function createPeerGroup(name: string, herd_ids: string[]): Promise<{ id: number; name: string }> {
  const { data } = await api.post("/api/benchmark/peer-groups", { name, herd_ids });
  return data;
}

export async function deletePeerGroup(id: number): Promise<void> {
  await api.delete(`/api/benchmark/peer-groups/${id}`);
}

export interface BubbleChartRow {
  herd_id: string;
  herd_name: string;
  size_class: string | null;
  region: string | null;
  month: string | null;
  x_val: number | null;
  y_val: number | null;
  size_val: number | null;
}

export interface BubbleChartResponse {
  x_kpi: string;
  y_kpi: string;
  size_kpi: string;
  month: string | null;
  month_from?: string | null;
  month_to?: string | null;
  rows: BubbleChartRow[];
}

export async function getBubbleChartData(params: {
  x_kpi: string;
  y_kpi: string;
  size_kpi: string;
  month?: string;
  month_from?: string;
  month_to?: string;
  size_filter?: string;
  peer_group_id?: number;
  own_herd_id?: string;
}): Promise<BubbleChartResponse> {
  const { data } = await api.get<BubbleChartResponse>("/api/benchmark/bubble-data", { params });
  return data;
}

// ── Repro ─────────────────────────────────────────────────────────────────────

export interface KpiTarget {
  id: number;
  herd_id: string;
  kpi_key: string;
  /** Stored database unit: fractions for percentage KPIs. */
  target_value: number;
  set_by: string | null;
  valid_until: string | null;
  created_at: string | null;
  /** The newest non-expired revision for its KPI. */
  is_current: boolean;
}

export interface KpiTargetResponse {
  targets: KpiTarget[];
  current_as_of: string;
  next_change_at: string | null;
}

export async function getKpiTargets(
  herdId: string,
  requestOptions?: ApiRequestOptions,
): Promise<KpiTargetResponse> {
  const { data } = await api.get<KpiTargetResponse>(
    `/api/herds/${herdId}/kpi-targets`,
    requestOptions as object,
  );
  return data;
}

export async function createKpiTarget(
  herdId: string,
  body: { kpi_key: string; target_value: number; valid_until?: string | null },
) {
  const { data } = await api.post(`/api/herds/${herdId}/kpi-targets`, body);
  return data;
}

export async function getReproNotes(
  herdId: string,
  params?: { kpi_key?: string; visibility?: string },
) {
  const { data } = await api.get(`/api/herds/${herdId}/repro/notes`, { params });
  return data;
}

export async function getReproKpiTargets(herdId: string) {
  return getKpiTargets(herdId);
}

export async function getReproMeetings(herdId: string) {
  const { data } = await api.get(`/api/herds/${herdId}/repro/meetings`);
  return data;
}

export async function createReproNote(
  herdId: string,
  body: { note_text: string; kpi_key?: string | null; visibility?: string },
) {
  const { data } = await api.post(`/api/herds/${herdId}/repro/notes`, body);
  return data;
}

export async function deleteReproNote(herdId: string, noteId: number) {
  await api.delete(`/api/herds/${herdId}/repro/notes/${noteId}`);
}

export async function createReproMeeting(
  herdId: string,
  body: { meeting_date: string; notes?: string; author?: string },
) {
  const { data } = await api.post(`/api/herds/${herdId}/repro/meetings`, body);
  return data;
}

export async function deleteReproMeeting(herdId: string, meetingId: number) {
  await api.delete(`/api/herds/${herdId}/repro/meetings/${meetingId}`);
}

export async function createReproKpiTarget(
  herdId: string,
  body: { kpi_key: string; target_value: number; valid_until?: string | null; set_by?: string },
) {
  return createKpiTarget(herdId, body);
}

export async function getReproAdminOverview() {
  const { data } = await api.get("/api/repro/admin-overview");
  return data;
}

// ── Genetics ──────────────────────────────────────────────────────────────────

export async function getHerdGenetics(herdId: string) {
  const { data } = await api.get(`/api/herds/${herdId}/genetics`);
  return data;
}

export async function getHerdGenomics(herdId: string) {
  const { data } = await api.get(`/api/herds/${herdId}/genomics`);
  return data;
}

export interface GenetikEvalMeta {
  file_name: string;
  uploaded_by: string | null;
  uploaded_at: string | null;
  data: Record<string, unknown>[];
}

export interface GenetikEvalResponse {
  enabled: boolean;
  upload: GenetikEvalMeta | null;
}

export interface GenetikEvalFarmMapping {
  sub_customer: string;
  herd_id: string | null;
  confirmed: boolean;
  suggested_herd_id: string | null;
  suggested_herd_name: string | null;
}

export interface GenetikEvalMappingsResponse {
  farms: GenetikEvalFarmMapping[];
  herds: { herd_id: string; herd_name: string }[];
}

export async function getGenetikEvalData(
  herdId?: string | null,
): Promise<GenetikEvalResponse> {
  const { data } = await api.get("/api/genetik-eval/data", {
    params: herdId ? { herd_id: herdId } : undefined,
  });
  return data;
}

export async function saveGenetikEvalData(
  fileName: string,
  rows: Record<string, unknown>[],
): Promise<void> {
  await api.post("/api/genetik-eval", { file_name: fileName, data: rows });
}

export async function deleteGenetikEvalData(): Promise<void> {
  await api.delete("/api/genetik-eval");
}

export async function getGenetikEvalMappings(): Promise<GenetikEvalMappingsResponse> {
  const { data } = await api.get("/api/genetik-eval/mappings");
  return data;
}

export async function saveGenetikEvalMappings(
  mappings: { sub_customer: string; herd_id: string | null }[],
): Promise<void> {
  await api.put("/api/genetik-eval/mappings", { mappings });
}

export interface FertilityDimRow {
  kpi_code: "preg_rate" | "brunstnutzung" | "konzeptionsrate";
  dim_bucket: string;
  month: string;
  month_date: string;
  /** Decimal fraction 0–1 (pct-cols convention). */
  value: number | null;
  numerator: number | null;
  denominator: number | null;
}

export async function getHerdFertilityDim(
  herdId: string,
  requestOptions?: ApiRequestOptions,
): Promise<FertilityDimRow[]> {
  const { data } = await api.get(`/api/herds/${herdId}/fertility-dim`, requestOptions as object);
  return data;
}

export interface FertilityGroupsResponse {
  /** Only rows whose rate is reproducible from source numerator/denominator counts. */
  rows: FertilityGroupRow[];
  /** Dimensions backed by validated source counts. */
  available_dimensions: FertilityGroupDimension[];
  /** Dimensions that must not be rendered as group values. */
  unavailable_dimensions: Array<{
    dimension: FertilityGroupDimension;
    reason: string;
  }>;
}

export async function getHerdFertilityGroups(
  herdId: string,
  params?: { herd_ids?: string },
  requestOptions?: ApiRequestOptions,
): Promise<FertilityGroupsResponse> {
  const { data } = await api.get(`/api/herds/${herdId}/fertility-groups`, { params, ...requestOptions } as object);
  return data;
}

export async function getHerdDryPeriod(herdId: string) {
  const { data } = await api.get(`/api/herds/${herdId}/dry-period`);
  return data;
}

// ── Sync ─────────────────────────────────────────────────────────────────────

export interface SyncJob {
  job_id: string;
  status: string;
  started_at: string;
  finished_at?: string | null;
  current_step?: number | null;
  total_steps?: number | null;
  progress?: string | null;
  name?: string | null;
  last_heartbeat?: string | null;
  stage_index?: number | null;
  stage_total?: number | null;
  stage_name?: string | null;
  herd_current?: number | null;
  herd_total?: number | null;
  /** Completed-stage weighted progress; deliberately not an ETA. */
  overall_percent?: number | null;
  /** Structured runtime accounting, excluding queue/watchdog interruption time. */
  active_elapsed_seconds?: number | null;
  waiting_elapsed_seconds?: number | null;
  paused_elapsed_seconds?: number | null;
  stage_elapsed_seconds?: number | null;
  eta_seconds?: number | null;
  eta_basis?: "current_stage_rate" | "current_stage_rate_plus_history_average" | "stage_history_average" | null;
}

export interface SyncStatusResponse {
  active_job: SyncJob | null;
  recent_jobs: SyncJob[];
  /** True solange der Watchdog den Sync Worker per SIGSTOP pausiert hat (Systemlast). */
  worker_paused?: boolean;
  /** True wenn Auto-Resume-Syncs jetzt laufen dürfen (Nachtfenster offen). */
  in_night_window?: boolean;
  /** True während der Karenzzeit nach Fensterende (Fortsetzungen laufen sofort). */
  in_resume_grace?: boolean;
  /** Anzeige-Label des Nachtfensters, z. B. "20:00–06:00". */
  night_window?: string;
}

export async function getSyncStatus(options?: ApiRequestOptions, includeRecent = true): Promise<SyncStatusResponse> {
  const { data } = await api.get<SyncStatusResponse>("/api/sync/status", {
    skipErrorToast: true,
    params: { include_recent: includeRecent },
    ...options,
  } as object);
  return data;
}

export interface SyncSidebarStatusResponse {
  active_job: SyncJob | null;
  last_completed_job: SyncJob | null;
  /** True solange der Watchdog den Sync Worker per SIGSTOP pausiert hat. */
  worker_paused: boolean;
  /** True wenn Auto-Resume-Syncs jetzt laufen dürfen (Nachtfenster offen). */
  in_night_window: boolean;
  /** True während der Karenzzeit nach Fensterende. */
  in_resume_grace: boolean;
  /** Anzeige-Label des Nachtfensters, z. B. "20:00–06:00". */
  night_window: string;
}
export interface FarmRegistration {
  id: number;
  kundennummer: string;
  betriebsname: string;
  ansprechpartner: string;
  email: string;
  hms: string;
  land: string;
  aussendienst: string;
  notizen: string;
  status: string;
  created_at: string;
}

export async function getAdminFarms(status?: string): Promise<FarmRegistration[]> {
  const { data } = await api.get<FarmRegistration[]>("/api/admin/farms", {
    params: status ? { status } : undefined,
  });
  return data;
}

export async function patchAdminFarm(id: number, status: string): Promise<{ ok: boolean }> {
  const { data } = await api.patch<{ ok: boolean }>(`/api/admin/farms/${id}`, { status });
  return data;
}

export async function createFarm(body: {
  kundennummer: string;
  betriebsname: string;
  ansprechpartner: string;
  email: string;
  hms: string;
  land: string;
  aussendienst: string;
  notizen: string;
}): Promise<{ id: number }> {
  const { data } = await api.post<{ id: number }>("/api/admin/farms", body);
  return data;
}

// ── Manual KPI entry ──────────────────────────────────────────────────────────

export interface ManualEntryValues {
  milch_kg?: number | null;
  fett?: number | null;
  protein?: number | null;
  ecm_betrieb?: number | null;
  zellzahl?: number | null;
  lebensleistung_ecm?: number | null;
  abgang_zucht?: number | null;
  alter_abgang_monate?: number | null;
  laktation_abgang?: number | null;
  nutzungsdauer_abgang?: number | null;
  is_quarterly_input?: number | null;
}

export interface ManualEntryResponse {
  herd_id: string;
  year: number;
  month?: number;
  quarter?: number;
  values: ManualEntryValues;
}

export async function getManualEntry(
  herdId: string,
  year: number,
  month: number,
): Promise<ManualEntryResponse> {
  const { data } = await api.get<ManualEntryResponse>(
    `/api/herds/${herdId}/manual-entry/${year}/${month}`,
  );
  return data;
}

export async function getManualEntryQuarterly(
  herdId: string,
  year: number,
  quarter: number,
): Promise<ManualEntryResponse> {
  const { data } = await api.get<ManualEntryResponse>(
    `/api/herds/${herdId}/manual-entry/${year}/q/${quarter}`,
  );
  return data;
}

export async function upsertManualEntry(
  herdId: string,
  year: number,
  month: number,
  values: ManualEntryValues,
): Promise<{ ok: boolean }> {
  const { data } = await api.put<{ ok: boolean }>(
    `/api/herds/${herdId}/manual-entry/${year}/${month}`,
    values,
  );
  return data;
}

export async function upsertManualEntryQuarterly(
  herdId: string,
  year: number,
  quarter: number,
  values: ManualEntryValues,
): Promise<{ ok: boolean }> {
  const { data } = await api.put<{ ok: boolean }>(
    `/api/herds/${herdId}/manual-entry/${year}/q/${quarter}`,
    values,
  );
  return data;
}

// ── Section order (per-herd) ──────────────────────────────────────────────────

export async function getSectionOrder(herdId: string, requestOptions?: ApiRequestOptions): Promise<string[] | null> {
  const { data } = await api.get<{ order: string[] | null }>(
    `/api/herds/${herdId}/section-order`, requestOptions as object,
  );
  return data.order;
}

export async function setSectionOrder(
  herdId: string,
  order: string[],
): Promise<{ ok: boolean }> {
  const { data } = await api.put<{ ok: boolean }>(
    `/api/herds/${herdId}/section-order`,
    { order },
  );
  return data;
}

export async function getHiddenSections(herdId: string, requestOptions?: ApiRequestOptions): Promise<string[]> {
  const { data } = await api.get<{ hidden: string[] }>(
    `/api/herds/${herdId}/hidden-sections`, requestOptions as object,
  );
  return data.hidden ?? [];
}

export async function setHiddenSections(
  herdId: string,
  hidden: string[],
): Promise<{ ok: boolean }> {
  const { data } = await api.put<{ ok: boolean }>(
    `/api/herds/${herdId}/hidden-sections`,
    { hidden },
  );
  return data;
}

// ── Lebensleistung ────────────────────────────────────────────────────────────

export interface LebensleistungEntry {
  month_date: string;
  lebensleistung_ecm: number;
}

export interface LebensleistungBenchmark {
  month_date: string;
  herd_count: number;
  p10: number | null;
  p25: number | null;
  avg: number | null;
  p75: number | null;
  p90: number | null;
  top_farm: number | null;
}

export interface LebensleistungResponse {
  herd_id: string;
  herd_data: LebensleistungEntry[];
  benchmark: LebensleistungBenchmark[];
}

export async function getLebensleistungData(
  herdId: string,
  requestOptions?: ApiRequestOptions,
): Promise<LebensleistungResponse> {
  const { data } = await api.get<LebensleistungResponse>(
    `/api/herds/${herdId}/lebensleistung`, requestOptions as object,
  );
  return data;
}

// ── KPI Export ────────────────────────────────────────────────────────────────

export interface KpiExportStatus {
  status: string | null;
  log?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
  failed_kpis?: string[];
  csv_filename?: string | null;
  merged_csv_filename?: string | null;
  merge_status?: string | null;
}

export interface KpiExportFile {
  filename: string;
  size_bytes: number;
  modified_at: string;
  download_url: string | null;
}

export async function getKpiExportStatus(): Promise<KpiExportStatus> {
  const { data } = await api.get<KpiExportStatus>("/api/admin/kpi-export/status");
  return data;
}

export async function getKpiExportFiles(): Promise<KpiExportFile[]> {
  const { data } = await api.get<KpiExportFile[]>("/api/admin/kpi-export/files");
  return data;
}

export async function triggerKpiExport(): Promise<{ ok: boolean }> {
  const { data } = await api.post<{ ok: boolean }>("/api/admin/kpi-export/trigger");
  return data;
}

// ── Admin ─────────────────────────────────────────────────────────────────────

export async function getAdminUsers() {
  const { data } = await api.get("/api/admin/users");
  return data;
}

export async function updateAdminUser(
  userId: number,
  body: {
    username?: string;
    full_name?: string;
    role?: string;
    is_active?: boolean;
    must_change_password?: boolean;
    email?: string;
    new_password?: string;
    country?: string | null;
  },
) {
  const { data } = await api.patch(`/api/admin/users/${userId}`, body);
  return data;
}

export async function deleteAdminUser(userId: number) {
  const { data } = await api.delete(`/api/admin/users/${userId}`);
  return data;
}

export async function getSectionVisibility(): Promise<Record<string, boolean>> {
  const { data } = await api.get("/api/admin/section-visibility");
  return data;
}

export async function getSectionVisibilityPublic(
  requestOptions?: ApiRequestOptions,
): Promise<Record<string, boolean>> {
  const { data } = await api.get("/api/section-visibility", requestOptions as object);
  return data;
}

export async function updateSectionVisibility(visibility: Record<string, boolean>) {
  const { data } = await api.put("/api/admin/section-visibility", { visibility });
  return data;
}

// ── AI Summary ────────────────────────────────────────────────────────────────

export interface AiSummaryResult {
  gesamtbewertung: string;
  staerken: string[];
  handlungsbedarf: string[];
  benchmark_vergleich: string;
  empfehlungen: string[];
  quellen_hinweis?: string;
}

export interface AiKpiFact {
  kpi: string;
  label: string;
  value_fmt: string;
  status: string;
  bench_avg_fmt: string;
  bench_gap_abs_fmt: string;
  bench_gap_pct_fmt: string;
  trend_direction: string;
  trend_magnitude_fmt: string;
}

export interface AiSource {
  doc_name: string;
  excerpt: string;
  source_type?: "file" | "url";
}

export interface AiUnavailableSource {
  doc_name: string;
  url?: string;
}

export interface AiSummaryResponse {
  herd_id: string;
  period: string;
  summary: AiSummaryResult;
  facts: AiKpiFact[];
  verified: boolean;
  cached: boolean;
  rag_active: boolean;
  sources: AiSource[];
  unavailable_sources?: AiUnavailableSource[];
}

export async function getAiSummary(
  herdId: string,
  params?: { months?: string; force_refresh?: boolean },
  requestOptions?: ApiRequestOptions,
): Promise<AiSummaryResponse> {
  const { data } = await api.get<AiSummaryResponse>(
    `/api/herds/${herdId}/ai-summary`,
    { params, ...requestOptions } as object,
  );
  return data;
}

// ── Analyse ───────────────────────────────────────────────────────────────────

export interface TuPlusMonthlyMeanHistoryEntry {
  month: string;
  value: number;
  source_period: "Month";
  semantics: "monthly_mean";
}

export interface CalvingForecastSourceRow {
  month: string;
  raw_month?: string;
  source_period?: "Month";
  erw_kalbung_m1?: number | null;
  erw_kalbung_m2?: number | null;
  erw_kalbung_m3?: number | null;
  erw_kalbung_m4?: number | null;
  erw_kalbung_m5?: number | null;
  erw_kalbung_m6?: number | null;
  erw_kalbung_m7?: number | null;
  erw_kalbung_rinder_m1?: number | null;
  erw_kalbung_rinder_m2?: number | null;
  erw_kalbung_rinder_m3?: number | null;
  erw_kalbung_rinder_m4?: number | null;
  erw_kalbung_rinder_m5?: number | null;
  erw_kalbung_rinder_m6?: number | null;
  erw_kalbung_rinder_m7?: number | null;
  source_coverage?: {
    complete?: boolean;
    covered_source_values?: number;
    expected_source_values?: number;
    fields?: Record<string, unknown>;
  };
  valid?: boolean;
}

export interface AnalyseResponse {
  herd_id: string;
  herd?: Herd;
  data_status?: KpiDataStatus;
  coverage_version?: string | null;
  coverage_hash?: string | null;
  period: string;
  herd_series: Record<string, unknown>[];
  /** Immutable standard core includes these point-in-time herd snapshots. */
  herd_snapshots?: Record<string, unknown>[];
  benchmark_series: Record<string, unknown>[];
  available_months: string[];
  benchmark_bands_series: Record<string, unknown>[];
  /** Exact-root factual deep dives. */
  fertility_dim?: FertilityDimRow[];
  fertility_groups?: FertilityGroupRow[];
  /** Historical TU+ counts, materialized with their source semantics in the pinned analyse root. */
  tu_plus_monthly_mean_history?: TuPlusMonthlyMeanHistoryEntry[];
  /** Root-captured, monthly-base calving forecasts; absent only in older roots. */
  calving_forecast_source_series?: CalvingForecastSourceRow[] | null;
  lebensleistung?: {
    herd_id: string;
    herd_data: Array<{ month_date: string; lebensleistung_ecm: number }>;
    benchmark: Record<string, unknown>[];
  };
  remontierung_snapshots?: RemontierungSnapshotsResponse[];
  benchmark_bands?: BenchmarkBandsQueryResponse;
  /** Aggregate-only provenance for root-materialised benchmark derivatives. */
  benchmark_inputs?: {
    schema_version: number;
    mode: "all";
    months: string[];
    cohort_version: string;
    nominal_peer_count: number;
  };
  /** Editable state frozen by the root's overlay_bundle_revision. */
  _overlay?: {
    kpi_targets?: KpiTargetResponse;
    section_order?: string[] | null;
    hidden_sections?: string[];
  };
  remontierung_contract?: {
    contract_id: string;
    contract_version: number;
    release_id: string | null;
    revision_id: string | null;
    facts_version: string;
    rule_version: string;
    source_mode: "legacy" | "revision";
    formula_id: string;
    unit_id: string;
    scale: number;
    coverage_status: string;
    aggregation_status: string;
  };
  remontierung_l1_drilldown?: {
    enabled: boolean;
    disabled_reason: string | null;
    quarters: Record<string, {
      revision_id: string;
      facts_version: string;
      rule_version: string;
      published_at: string | null;
      coverage_status: "complete" | "incomplete";
      chart_parity_status: "matched" | "mismatch";
      chart_l1_rate: number | null;
    }>;
  };
}

/**
 * Server-owned replacement-rate time series.
 *
 * Values are decimal fractions.  The server is the sole owner of window and
 * quarterly aggregation; clients must only format these snapshot values.
 */
export type RemontierungSnapshotMode = "rolling" | "quarter" | "month";
export type RemontierungSnapshotStatus = "ok" | "preliminary" | "partial" | "unavailable";

export interface RemontierungSnapshotPoint {
  point_id: string;
  series_id: "total" | "l1_total_herd" | "l1_within_l1" | "l2plus_direct";
  /** Stable server period key (month for rolling, YYYY-Qn for quarter). */
  period: string;
  period_kind?: "month" | "quarter_closed" | "quarter_preliminary" | "rolling_12";
  period_start?: string;
  period_end_exclusive?: string;
  window_months?: number;
  lactation_group: "L1+" | "L1" | "L2+";
  status: RemontierungSnapshotStatus;
  reason: string;
  preliminary?: boolean;
  /** Decimal-string rate: keep server precision until display conversion. */
  value: string | null;
  canonical_value: string | null;
  source_mode: "direct" | "legacy" | null;
}

export interface RemontierungSnapshotsResponse {
  api_version: "v1";
  snapshot_id: string;
  herd_id: string;
  herd_ids: string[];
  mode: RemontierungSnapshotMode | "month";
  formula_id: string;
  points: RemontierungSnapshotPoint[];
  peer_bands?: Array<{
    series_id: RemontierungSnapshotPoint["series_id"];
    period: string;
    nominal_count: number;
    eligible_count: number;
    valid_count: number;
    exclusion_reasons: Record<string, number>;
    p10: number | null; p25: number | null; p50: number | null;
    p75: number | null; p90: number | null;
    band_status: "insufficient" | "inner_only" | "full";
  }>;
  cohort_manifest?: {
    selector: { mode: "size_class" | "milking_system" | "peer_group"; value: string };
    cohort_version: string;
    nominal_count: number;
  };
}

export async function getRemontierungSnapshots(
  herdId: string,
  params: {
    mode: RemontierungSnapshotMode;
    from_month?: string | null;
    to_month?: string | null;
    herd_ids?: string[];
    cohort_mode?: "all" | "size_class" | "milking_system" | "peer_group" | null;
    cohort_value?: string | null;
  },
  requestOptions?: ApiRequestOptions,
): Promise<RemontierungSnapshotsResponse> {
  const { data } = await api.get<RemontierungSnapshotsResponse>(
    `/api/herds/${herdId}/remontierung/v1/snapshot`,
    { params: {
      mode: params.mode,
      from_month: params.from_month ?? undefined,
      to_month: params.to_month ?? undefined,
      // FastAPI's typed list query accepts repeated keys; axios serializes
      // this array accordingly when indexes are disabled below.
      herd_ids: params.herd_ids,
      cohort_mode: params.cohort_mode ?? undefined,
      cohort_value: params.cohort_value ?? undefined,
    }, paramsSerializer: { indexes: null }, ...requestOptions } as object,
  );
  return data;
}

export async function getPinnedRemontierungProjection(
  rootHash: string,
  herdId: string,
  params: {
    mode: RemontierungSnapshotMode;
    /** Keep chart-axis projection independent of the underlying mode. */
    display_period?: "month" | "quarter";
    from_month?: string | null;
    to_month?: string | null;
    herd_ids?: string[];
    cohort_mode?: "all" | "size_class" | "milking_system" | "peer_group" | null;
    cohort_value?: string | null;
  },
  requestOptions?: ApiRequestOptions,
): Promise<RemontierungSnapshotsResponse> {
  const options = snapshotOptions({
    skipErrorToast: requestOptions?.skipErrorToast ?? true,
    ...requestOptions,
  }, {
    herd: herdId,
    root: rootHash,
    scope: params.herd_ids ?? [herdId],
    mode: "remontierung-projection",
  }, "remontierung-projection");
  const { data } = await api.get<RemontierungSnapshotsResponse>(
    `/api/snapshots/${rootHash}/${herdId}/remontierung/v1/projection`,
    { params: {
      mode: params.mode,
      display_period: params.display_period,
      from_month: params.from_month ?? undefined,
      to_month: params.to_month ?? undefined,
      herd_ids: params.herd_ids,
      cohort_mode: params.cohort_mode ?? undefined,
      cohort_value: params.cohort_value ?? undefined,
    }, paramsSerializer: { indexes: null }, ...options } as object,
  );
  return data;
}

export async function exportRemontierungSnapshot(
  herdId: string,
  params: RemontierungExportContext & { format: "json" | "csv" },
): Promise<Blob> {
  if (params.root_hash) {
    const projected = await getPinnedRemontierungProjection(params.root_hash, herdId, {
      mode: params.remon_mode,
      from_month: params.from_month,
      to_month: params.to_month,
      herd_ids: params.herd_ids,
      cohort_mode: params.cohort_mode,
      cohort_value: params.cohort_value,
    });
    const columns = ["snapshot_id", "herd_id", "mode", "revision_id", "formula_id",
      "point_id", "series_id", "lactation_group", "period", "period_kind", "status",
      "reason", "numerator", "denominator", "canonical_value", "value", "unit"];
    const quote = (value: unknown) => {
      const text = value == null ? "" : typeof value === "object" ? JSON.stringify(value) : String(value);
      return `"${text.replaceAll("\"", "\"\"")}"`;
    };
    const lines = [columns.join(","), ...projected.points.map((point) => columns.map((column) =>
      quote((point as unknown as Record<string, unknown>)[column]
        ?? (projected as unknown as Record<string, unknown>)[column])).join(","))];
    return new Blob([`${lines.join("\n")}\n`], { type: "text/csv;charset=utf-8" });
  }
  const { data } = await api.get<Blob>(
    `/api/herds/${herdId}/remontierung/v1/export`,
    {
      params: {
        format: params.format,
        mode: params.remon_mode,
        from: params.from_month ?? undefined,
        to: params.to_month ?? undefined,
        herd_ids: params.herd_ids,
        // A pinned snapshot already contains its resolved cohort.
        snapshot_id: params.snapshot_id ?? undefined,
      },
      paramsSerializer: { indexes: null },
      responseType: "blob",
    },
  );
  return data;
}

export async function getHerdAnalyse(
  herdId: string,
  params?: {
    months?: string;
    period?: string;
    herd_ids?: string;
  },
  requestOptions?: ApiRequestOptions,
): Promise<AnalyseResponse> {
  const { data } = await api.get<AnalyseResponse>(
    `/api/herds/${herdId}/analyse`,
    { params, ...requestOptions } as object,
  );
  return data;
}

/**
 * Snapshot cores include the root-materialised, privacy-safe benchmark
 * derivatives.  This adapter intentionally never falls back to a live
 * benchmark request.
 */
export async function getSnapshotAnalyse(
  rootHash: string,
  herdId: string,
  requestOptions?: ApiRequestOptions,
): Promise<AnalyseResponse> {
  const core = await getSnapshotCore<Partial<AnalyseResponse>>(
    rootHash,
    herdId,
    "analyse",
    requestOptions,
  );
  return {
    herd_id: herdId,
    herd: core.herd,
    period: "monthly",
    herd_series: core.herd_series ?? [],
    available_months: core.available_months ?? [],
    herd_snapshots: core.herd_snapshots,
    benchmark_series: core.benchmark_series ?? [],
    benchmark_bands_series: core.benchmark_bands_series ?? [],
    fertility_dim: core.fertility_dim,
    fertility_groups: core.fertility_groups,
    tu_plus_monthly_mean_history: core.tu_plus_monthly_mean_history,
    calving_forecast_source_series: core.calving_forecast_source_series,
    lebensleistung: core.lebensleistung,
    remontierung_snapshots: core.remontierung_snapshots,
    _overlay: core._overlay,
    benchmark_bands: core.benchmark_bands,
    benchmark_inputs: core.benchmark_inputs,
    remontierung_contract: core.remontierung_contract,
    remontierung_l1_drilldown: core.remontierung_l1_drilldown,
  } as AnalyseResponse;
}

export async function getSnapshotMultiAnalyse(
  rootHash: string,
  herdIds: string[],
  overlayHash?: string,
  requestOptions?: ApiRequestOptions,
): Promise<SnapshotMultiCore<AnalyseResponse>> {
  const result = await getSnapshotMultiCore<Partial<AnalyseResponse>>(
    rootHash, herdIds, "analyse", overlayHash, requestOptions,
  );
  const normalize = (core: Partial<AnalyseResponse>, herdId: string): AnalyseResponse => ({
    herd_id: herdId,
    herd: core.herd,
    period: "monthly",
    herd_series: core.herd_series ?? [],
    available_months: core.available_months ?? [],
    herd_snapshots: core.herd_snapshots,
    benchmark_series: core.benchmark_series ?? [],
    benchmark_bands_series: core.benchmark_bands_series ?? [],
    fertility_dim: core.fertility_dim,
    fertility_groups: core.fertility_groups,
    tu_plus_monthly_mean_history: core.tu_plus_monthly_mean_history,
    calving_forecast_source_series: core.calving_forecast_source_series,
    lebensleistung: core.lebensleistung,
    remontierung_snapshots: core.remontierung_snapshots,
    _overlay: core._overlay,
    benchmark_bands: core.benchmark_bands,
    benchmark_inputs: core.benchmark_inputs,
    remontierung_contract: core.remontierung_contract,
    remontierung_l1_drilldown: core.remontierung_l1_drilldown,
  });
  return {
    data: normalize(result.data, herdIds[0] ?? ""),
    members: Object.fromEntries(
      Object.entries(result.members).map(([herdId, core]) => [herdId, normalize(core, herdId)]),
    ),
  };
}

export async function exportAnalysePdf(
  herdId: string,
  body?: {
    months?: string[];
    herd_ids?: string[];
    include_sections?: string[];
    farm_name?: string;
    period_label?: string;
    remontierung?: RemontierungExportContext;
    /** Use the immutable standard-data core rather than a live read. */
    root_hash?: string;
    overlay_hash?: string;
    snapshot_mode?: "legacy" | "pinned";
  },
): Promise<Blob> {
  const { data } = await api.post<Blob>(
    `/api/herds/${herdId}/export/pdf`,
    body ?? {},
    { responseType: "blob", readRecovery: false },
  );
  return data;
}

export interface CombinedPdfKpiTile {
  label: string;
  value: string;
  unit: string;
  status: string;
  period?: string;
}

export interface CombinedPdfRadarScore {
  label: string;
  score: number;
  category: string;
}

export interface CombinedPdfWarnEntry {
  text: string;
  status: string;
}

export interface CombinedPdfAiSummary {
  gesamtbewertung: string | null;
  staerken: string[];
  handlungsbedarf: string[];
  benchmark_vergleich: string | null;
  empfehlungen: string[];
  facts: { label: string; value: string; status: string }[];
}

/** A display-only target preference. Values and target metadata stay server-owned. */
export interface CombinedPdfTargetPreference {
  kpi_key: string;
  chart_id: string;
  target_revision_id: number;
  visible: boolean;
}

/** Strictly presentation-only export context; target preferences stay identity-only. */
export interface CombinedPdfDisplayContext {
  locale: "de" | "en" | "en-gb" | "nl" | "pl" | "it";
  unit_system: "metric" | "imperial";
}

export interface CombinedPdfBenchmarkQuery {
  scope: BenchmarkBandScope;
  period: "monthly" | "quarterly";
  months: string[];
  selections: BenchmarkBandSelection[];
}
/** Immutable snapshot selection accepted by the standard and combined PDF APIs. */
export interface PinnedSnapshotExport {
  root_hash?: string;
  overlay_hash?: string;
  snapshot_mode?: "legacy" | "pinned";
}
export interface RemontierungExportContext {
  remon_mode: "rolling" | "quarter" | "month";
  from_month: string | null;
  to_month: string | null;
  herd_ids?: string[];
  cohort_mode?: "all" | "size_class" | "milking_system" | "peer_group";
  cohort_value?: string;
  snapshot_id: string | null;
  root_hash?: string;
}
export async function exportCombinedPdf(
  herdId: string,
  body: PinnedSnapshotExport & {
    months?: string[];
    period_label?: string;
    farm_name?: string;
    kpi_tiles?: CombinedPdfKpiTile[];
    warning_entries?: CombinedPdfWarnEntry[];
    ai_summary?: CombinedPdfAiSummary | null;
    radar_scores?: CombinedPdfRadarScore[];
    active_kpi_key?: string;
    active_kpi_label?: string;
    show_top_farm?: boolean;
    include_sections?: string[];
    benchmark_query?: CombinedPdfBenchmarkQuery;
    /** Browser-rendered chart snapshots (id → PNG data URL) */
    chart_images?: Record<string, string>;
    /** Target-line visibility, scoped to the currently confirmed revision. */
    target_preferences?: CombinedPdfTargetPreference[];
    /** Locale and units for server-rendered PDF fallback charts. */
    display_context?: CombinedPdfDisplayContext;
    remontierung?: RemontierungExportContext;
  },
): Promise<Blob> {
  const { data } = await api.post<Blob>(
    `/api/herds/${herdId}/export/combined-pdf`,
    body,
    { responseType: "blob", readRecovery: false },
  );
  return data;
}

export async function exportSummaryPdf(
  herdId: string,
  body: PinnedSnapshotExport & {
    months?: string[];
    period_label?: string;
    active_kpi_key?: string;
    scorecard_slots?: string[];
    benchmark_query?: CombinedPdfBenchmarkQuery;
    remontierung?: RemontierungExportContext;
  },
): Promise<Blob> {
  const { data } = await api.post<Blob>(
    `/api/herds/${herdId}/export/summary-pdf`,
    body,
    { responseType: "blob", readRecovery: false },
  );
  return data;
}

// ── Action Plan ───────────────────────────────────────────────────────────────

export interface ActionPlanCriticalKpi {
  kpi_key: string;
  label: string;
  value: number;
  status: string;
  benchmark_avg: number | null;
  threshold_green: number | null;
  threshold_yellow: number | null;
  lower_is_better: boolean;
  product_recommendation?: ProductRecommendation | null;
}

export interface ActionPlanNote {
  id: number;
  herd_id: string;
  note_text: string;
  kpi_key: string | null;
  kpi_label: string | null;
  status: "open" | "done";
  priority: "low" | "medium" | "high";
  note_type: "note" | "action_step";
  autopilot_section: string | null;
  autopilot_kpi: string | null;
  created_at: string;
  updated_at: string;
}

export interface ActionPlanAdvisoryKpi {
  kpi_key: string;
  label: string;
  value: number;
  status: string;
  benchmark_avg: number | null;
  threshold_green: number | null;
  threshold_yellow: number | null;
  lower_is_better: boolean;
  product_recommendation: ProductRecommendation;
}

export interface ActionPlanResponse {
  herd_id: string;
  herd_name: string | null;
  latest_month: string | null;
  critical_kpis: ActionPlanCriticalKpi[];
  advisory_kpis: ActionPlanAdvisoryKpi[];
  notes: ActionPlanNote[];
}

export async function getActionPlan(herdId: string): Promise<ActionPlanResponse> {
  const { data } = await api.get<ActionPlanResponse>(`/api/herds/${herdId}/action-plan`);
  return data;
}

export async function getActionPlanNotes(herdId: string): Promise<ActionPlanNote[]> {
  const { data } = await api.get<ActionPlanNote[]>(`/api/herds/${herdId}/action-plan/notes`);
  return data;
}

export async function createActionPlanNote(
  herdId: string,
  body: {
    note_text: string;
    kpi_key?: string | null;
    priority?: string | null;
    note_type?: "note" | "action_step" | null;
    autopilot_section?: string | null;
    autopilot_kpi?: string | null;
  },
): Promise<{ id: number; herd_id: string; kpi_key: string | null }> {
  const { data } = await api.post(`/api/herds/${herdId}/action-plan/notes`, body);
  return data;
}

export async function getSlideNotes(
  herdId: string,
  section: string,
  kpi: string,
): Promise<ActionPlanNote[]> {
  const { data } = await api.get<ActionPlanNote[]>(
    `/api/herds/${herdId}/action-plan/slide-notes`,
    { params: { section, kpi } },
  );
  return data;
}

export async function upsertSlideNote(
  herdId: string,
  section: string,
  kpi: string,
  noteText: string,
): Promise<{ id: number; created: boolean }> {
  const { data } = await api.put(
    `/api/herds/${herdId}/action-plan/slide-note`,
    { section, kpi, note_text: noteText },
  );
  return data;
}

export async function updateActionPlanNote(
  herdId: string,
  noteId: number,
  patch: { note_text?: string; status?: "open" | "done"; priority?: "low" | "medium" | "high" },
): Promise<{ ok: boolean }> {
  const { data } = await api.patch(
    `/api/herds/${herdId}/action-plan/notes/${noteId}`,
    patch,
  );
  return data;
}

export async function deleteActionPlanNote(herdId: string, noteId: number): Promise<void> {
  await api.delete(`/api/herds/${herdId}/action-plan/notes/${noteId}`);
}

export async function getAiActionPlanSuggestion(
  herdId: string,
  body: { kpi_key: string; current_value: number; benchmark_avg?: number | null },
): Promise<{ herd_id: string; kpi_key: string; empfehlungen: string[] }> {
  const { data } = await api.post(`/api/herds/${herdId}/action-plan/ai-suggestion`, body);
  return data;
}

// ── Audit ──────────────────────────────────────────────────────────────────────

export async function auditEvent(
  action: string,
  target?: string,
  details?: Record<string, unknown>,
): Promise<void> {
  await api.post("/api/audit/event", { action, target, details });
}

// ── User preferences ──────────────────────────────────────────────────────────

export async function getPref<T = unknown>(
  key: string,
  requestOptions?: ApiRequestOptions,
): Promise<T | null> {
  const { data } = await api.get<{ key: string; value: T | null }>(
    `/api/prefs/${key}`,
    requestOptions as object,
  );
  return data.value;
}

export async function setPref<T = unknown>(key: string, value: T): Promise<void> {
  await api.put(`/api/prefs/${key}`, { value });
}

// ── Admin: Sync ────────────────────────────────────────────────────────────────

export interface SyncJobFull extends SyncJob {
  job_type?: string | null;
  finished_at?: string | null;
  progress?: string | null;
  records?: number | null;
  current_endpoint?: string | null;
  cancel_requested?: number | null;
  live_log?: string | null;
  job_params?: string | null;
}

export interface SyncHistoryResponse {
  jobs: SyncJobFull[];
  count: number;
}

export async function getSyncHistory(limit = 50, options?: ApiRequestOptions): Promise<SyncHistoryResponse> {
  const { data } = await api.get<SyncHistoryResponse>("/api/sync/history", {
    params: { limit },
    skipErrorToast: true,
    ...options,
  } as object);
  return data;
}

export async function startDeltaSync(): Promise<{ job_id: string; message: string }> {
  const { data } = await api.post("/api/sync/start-delta", undefined, {
    skipErrorToast: true,
  } as object);
  return data;
}

export async function startSync(): Promise<{ job_id: string; message: string }> {
  const { data } = await api.post("/api/sync/start", undefined, {
    skipErrorToast: true,
  } as object);
  return data;
}

export async function startKpiSync(): Promise<{ job_id: string; message: string }> {
  const { data } = await api.post("/api/sync/start-kpi", undefined, {
    skipErrorToast: true,
  } as object);
  return data;
}

export async function cancelSyncJob(jobId: string): Promise<void> {
  await api.post(`/api/sync/jobs/${jobId}/cancel`, undefined, {
    skipErrorToast: true,
  } as object);
}

export async function getSyncJobLog(jobId: string, options?: ApiRequestOptions): Promise<{ job_id: string; log: string }> {
  const { data } = await api.get(`/api/sync/jobs/${jobId}/log`, {
    skipErrorToast: true,
    ...options,
  } as object);
  return data;
}

export interface ScheduleEntry {
  id: number;
  job_type: string;
  display_name: string;
  frequency: string;
  preferred_hour: number;
  preferred_weekdays: number[];
  depends_on: string[];
  enabled: boolean;
  last_queued_at: string | null;
  last_finished_at: string | null;
  last_status: string | null;
  notes: string | null;
}

export async function getSyncSchedule(options?: ApiRequestOptions): Promise<ScheduleEntry[]> {
  const { data } = await api.get<ScheduleEntry[]>("/api/admin/sync/schedule", options);
  return data;
}

export async function updateSyncSchedule(scheduleId: number, body: { enabled: boolean; preferred_hour: number }) {
  const { data } = await api.put(`/api/admin/sync/schedule/${scheduleId}`, body);
  return data;
}

export interface SyncFailure {
  job_id: string;
  name: string;
  status: string;
  job_type: string | null;
  started_at: string | null;
  finished_at: string | null;
  progress: string | null;
  records: number | null;
  current_endpoint: string | null;
}

export async function getSyncFailures(days = 7, options?: ApiRequestOptions): Promise<{ failures: SyncFailure[]; count: number }> {
  const { data } = await api.get("/api/admin/sync/failures", { params: { days }, ...options });
  return data;
}

export interface WorkerRestart {
  id: number;
  restarted_at: string | null;
  exit_code: number | null;
  signal_name: string | null;
  restart_reason: string | null;
}

export async function getWorkerRestarts(options?: ApiRequestOptions): Promise<{ restarts: WorkerRestart[]; count: number }> {
  const { data } = await api.get("/api/admin/sync/worker-restarts", options);
  return data;
}

export interface StorageForecast {
  status: "ok" | "warning";
  used_bytes: number;
  limit_bytes: number;
  free_bytes: number;
  minimum_headroom_bytes: number;
  used_percent: number;
  history: Array<{ date: string; size_bytes: number }>;
  retention_days: number;
  average_daily_growth_bytes: number;
  last_nightly_growth_bytes: number;
  expected_next_growth_bytes: number;
  days_to_warning: number | null;
  forecast_alert: boolean;
}

export async function getStorageForecast(options?: ApiRequestOptions): Promise<StorageForecast> {
  const { data } = await api.get<StorageForecast>("/api/admin/sync/storage-forecast", options);
  return data;
}

// ── Admin: Price history ───────────────────────────────────────────────────────

export interface PriceHistoryEntry {
  id: number;
  herd_id: string;
  valid_from: string;
  milk_price?: number | null;
  feed_cost?: number | null;
}

export async function getPriceHistory(herdId: string, kind: "milk" | "feed" = "milk"): Promise<PriceHistoryEntry[]> {
  const { data } = await api.get<PriceHistoryEntry[]>("/api/admin/price-history", { params: { herd_id: herdId, kind } });
  return data;
}

export async function addPriceHistoryEntry(body: { herd_id: string; valid_from: string; value: number; kind: string }) {
  const { data } = await api.post("/api/admin/price-history", body);
  return data;
}

export async function updatePriceHistoryEntry(id: number, body: { valid_from: string; value: number; kind: string }) {
  const { data } = await api.put(`/api/admin/price-history/${id}`, body);
  return data;
}

export async function deletePriceHistoryEntry(id: number, kind: "milk" | "feed" = "milk") {
  await api.delete(`/api/admin/price-history/${id}`, { params: { kind } });
}

// ── Admin: KPI Thresholds ──────────────────────────────────────────────────────

export interface KpiThreshold {
  kpi_key: string;
  green: number | null;
  yellow: number | null;
  direction: string | null;
}

export async function getKpiThresholds(): Promise<Record<string, KpiThreshold>> {
  const { data } = await api.get("/api/admin/kpi-thresholds");
  return data;
}

export async function saveKpiThresholds(thresholds: Record<string, Partial<KpiThreshold>>) {
  const { data } = await api.put("/api/admin/kpi-thresholds", { thresholds });
  return data;
}

// ── Admin: Herd economics ──────────────────────────────────────────────────────

export interface HerdEconomics {
  herd_id: string;
  milk_price: number | null;
  heifer_cost: number | null;
  cull_revenue: number | null;
}

export async function updateHerdSettings(herdId: string, body: {
  milking_system?: string;
  is_active?: number;
  region?: string;
  source_status?: Herd["source_status"];
}) {
  const { data } = await api.patch(`/api/admin/herds/${herdId}`, body);
  return data;
}

export async function getHerdEconomics(herdId: string): Promise<HerdEconomics> {
  const { data } = await api.get<HerdEconomics>(`/api/admin/herds/${herdId}/economics`);
  return data;
}

export async function saveHerdEconomics(herdId: string, body: Omit<HerdEconomics, "herd_id">) {
  const { data } = await api.put(`/api/admin/herds/${herdId}/economics`, body);
  return data;
}

// ── Admin: Freshness snapshot ──────────────────────────────────────────────────

export interface FreshnessRow {
  herd_id: string;
  herd_name: string;
  last_kpi_date: string | null;
  last_icdm_date: string | null;
  last_ok_sync: string | null;
  kpi_age_days: number | null;
  icdm_age_days: number | null;
  is_stale: boolean;
}

export interface FreshnessSnapshot {
  rows: FreshnessRow[];
  stale_days: number;
  kpi_stale_days: number;
  computed_at: string | null;
  total: number;
  stale_count: number;
}

export async function getFreshnessSnapshot(options?: ApiRequestOptions): Promise<FreshnessSnapshot> {
  const { data } = await api.get<FreshnessSnapshot>("/api/admin/freshness-snapshot", options);
  return data;
}

// ── Admin: Analytics ──────────────────────────────────────────────────────────

export interface UsageSession {
  id: number;
  herd_id: string | null;
  mitarbeiter: string | null;
  start_time: string | null;
  end_time: string | null;
  pages_visited: string | null;
}

export interface PdfExportEntry {
  id: number;
  herd_id: string | null;
  mitarbeiter: string | null;
  exported_at: string | null;
}

export interface AnalyticsResponse {
  sessions: UsageSession[];
  pdf_exports: PdfExportEntry[];
  sessions_count: number;
  pdf_exports_count: number;
  days: number;
}

export async function getAdminAnalytics(days = 30): Promise<AnalyticsResponse> {
  const { data } = await api.get<AnalyticsResponse>("/api/admin/analytics", { params: { days } });
  return data;
}

// ── Admin: Translations ────────────────────────────────────────────────────────

export interface TranslationEntry {
  section: string;
  key: string;
  lang: string;
  value: string;
  base_value: string;
  is_override: boolean;
}

export async function getTranslations(params?: { section?: string; lang?: string }): Promise<TranslationEntry[]> {
  const { data } = await api.get<TranslationEntry[]>("/api/admin/translations", { params });
  return data;
}

export async function updateTranslation(key: string, body: { value: string; section: string; lang: string }) {
  const { data } = await api.put(`/api/admin/translations/${encodeURIComponent(key)}`, body);
  return data;
}

export async function exportTranslations(): Promise<Blob> {
  const { data } = await api.get("/api/admin/translations/export", { responseType: "blob" });
  return data;
}

export async function importTranslations(file: File): Promise<{ ok: boolean; stats: Record<string, unknown>; warnings: string[]; diff_count: number }> {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post("/api/admin/translations/import", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export interface TranslationProgressEntry {
  lang: string;
  total_keys: number;
  translated_keys: number;
  percent: number;
  missing: { section: string; key: string }[];
}

export async function getTranslationProgress(): Promise<TranslationProgressEntry[]> {
  const { data } = await api.get<TranslationProgressEntry[]>("/api/admin/translations/progress");
  return data;
}

// ── Admin: Audit log ──────────────────────────────────────────────────────────

export interface AuditLogEntry {
  id: number;
  action: string;
  actor: string | null;
  target: string | null;
  details: Record<string, unknown> | null;
  created_at: string | null;
}

export async function getAuditLog(params?: { limit?: number; action?: string }): Promise<AuditLogEntry[]> {
  const { data } = await api.get<AuditLogEntry[]>("/api/admin/audit-log", { params });
  return data;
}

// ── Kundenliste / ADM-Zuweisung ───────────────────────────────────────────────

export interface KundenlisteNewUser {
  user_id: number;
  username: string;
  full_name: string;
  password: string;
}

export interface KundenlisteImportResult {
  assigned: number;
  weitere: number;
  new_users: KundenlisteNewUser[];
  unmatched_herds: string[];
  row_errors: string[];
}

export interface AdmBetrieb {
  herd_id: string;
  herd_name: string;
  kundennummer: string | null;
  user_id: number | null;
  username: string | null;
  full_name: string | null;
}

export async function uploadKundenliste(file: File): Promise<KundenlisteImportResult> {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post<KundenlisteImportResult>(
    "/api/admin/kundenliste/upload",
    form,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return data;
}

export async function getAdmBetriebe(): Promise<AdmBetrieb[]> {
  const { data } = await api.get<AdmBetrieb[]>("/api/admin/kundenliste/betriebe");
  return data;
}

export async function patchAdmBetrieb(herd_id: string, user_id: number | null): Promise<void> {
  await api.patch(`/api/admin/kundenliste/betriebe/${herd_id}`, { user_id });
}

// ── Knowledge Library (RAG) ───────────────────────────────────────────────────

export interface KnowledgeDoc {
  id: number;
  filename: string;
  content_hash: string;
  uploaded_at: string | null;
  uploaded_by: string | null;
  chunk_count: number;
  enabled: boolean;
  source_type?: "file" | "url";
  url?: string | null;
}

export async function addKnowledgeUrl(title: string, url: string): Promise<{
  doc_id: number;
  title: string;
  url: string;
  reachable: boolean;
  probe_error: string | null;
}> {
  const { data } = await api.post("/api/admin/knowledge/url", { title, url });
  return data;
}

export async function listKnowledgeDocs(): Promise<KnowledgeDoc[]> {
  const { data } = await api.get<KnowledgeDoc[]>("/api/admin/knowledge/docs");
  return data;
}

export async function uploadKnowledgeDoc(file: File): Promise<{ doc_id: number; filename: string; chunk_count: number }> {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post("/api/admin/knowledge/upload", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function deleteKnowledgeDoc(id: number): Promise<void> {
  await api.delete(`/api/admin/knowledge/docs/${id}`);
}

export async function toggleKnowledgeDoc(id: number, enabled: boolean): Promise<void> {
  await api.patch(`/api/admin/knowledge/docs/${id}`, { enabled });
}

export async function reindexKnowledgeDoc(id: number): Promise<{ doc_id: number; chunk_count: number; status: string }> {
  const { data } = await api.post(`/api/admin/knowledge/reindex/${id}`);
  return data;
}

// ── HNSW Index Health ─────────────────────────────────────────────────────────

// ── User Feedback ─────────────────────────────────────────────────────────────

export interface FeedbackEntry {
  id: number;
  sender_username: string;
  sender_display: string;
  herd_id: string | null;
  herd_name: string | null;
  page: string | null;
  section: string | null;
  message: string;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
  reply_text: string | null;
  reply_by: string | null;
  reply_at: string | null;
  has_screenshot: boolean;
}

export interface MyFeedbackEntry {
  id: number;
  herd_id: string | null;
  herd_name: string | null;
  page: string | null;
  section: string | null;
  message: string;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
  reply_text: string | null;
  reply_by: string | null;
  reply_at: string | null;
  reply_read: boolean;
  has_screenshot: boolean;
}

export interface FeedbackSubmitBody {
  message: string;
  herd_id?: string;
  herd_name?: string;
  page?: string;
  section?: string;
  screenshot_b64?: string;
}

export async function submitFeedback(body: FeedbackSubmitBody): Promise<{ id: number }> {
  const { data } = await api.post<{ id: number }>("/api/feedback", body);
  return data;
}

export async function listFeedback(): Promise<FeedbackEntry[]> {
  const { data } = await api.get<FeedbackEntry[]>("/api/feedback");
  return data;
}

export async function markFeedbackRead(id: number): Promise<void> {
  await api.patch(`/api/feedback/${id}/read`);
  sessionStatusReads.delete("feedback-unread");
}

export async function getUnreadFeedbackCount(): Promise<number> {
  try {
    return await shortSessionStatusRead("feedback-unread", async () => {
      const { data } = await api.get<{ count: number }>(
        "/api/feedback/unread-count",
        { skipErrorToast: true } as object,
      );
      return data.count;
    });
  } catch {
    return 0;
  }
}

export async function getFeedbackScreenshot(id: number): Promise<{ screenshot_b64: string }> {
  const { data } = await api.get<{ screenshot_b64: string }>(`/api/feedback/${id}/screenshot`);
  return data;
}

export async function listMyFeedback(): Promise<MyFeedbackEntry[]> {
  const { data } = await api.get<MyFeedbackEntry[]>("/api/feedback/mine");
  return data;
}

export async function getMyFeedbackReplyCount(): Promise<number> {
  try {
    const { data } = await api.get<{ count: number }>(
      "/api/feedback/my-reply-count",
      { skipErrorToast: true } as object,
    );
    return data.count;
  } catch {
    return 0;
  }
}

export async function markFeedbackRepliesSeen(): Promise<void> {
  await api.patch("/api/feedback/replies/seen");
}

export async function replyToFeedback(id: number, reply: string): Promise<void> {
  await api.post(`/api/feedback/${id}/reply`, { reply });
}

export async function checkHnswIndexes(options?: ApiRequestOptions): Promise<HnswIndexStatus> {
  // Preserve the main project's defensive response normalization. HTTP and
  // transport failures still reject so the Admin coordinator owns recovery.
  const { data, status } = await api.get<unknown>("/health/hnsw-indexes", options);
  return normalizeHnswIndexStatus(data, status);
}

// ---------------------------------------------------------------------------
// Daten-Qualitäts-Agent
// ---------------------------------------------------------------------------

export interface DqRunFull {
  run: DqRun | null;
  issues: DqIssue[];
  proposals: DqProposal[];
}

export interface DqRun {
  id: number;
  run_id: string;
  triggered_by: string;
  job_id: string | null;
  started_at: string | null;
  finished_at: string | null;
  n_critical: number;
  n_delayed: number;
  n_hint: number;
  n_total: number;
  n_proposals: number;
}

export interface DqIssue {
  id: number;
  run_id: string;
  herd_id: string;
  herd_name: string | null;
  issue_type: string;
  kpi_key: string | null;
  period: string | null;
  severity: string;
  detail: string;
  created_at: string | null;
}

export interface DqProposal {
  id: number;
  run_id: string;
  herd_id: string;
  kpi_key: string;
  target_month: string;
  proposed_value: number;
  source_type: string;
  source_month: string;
  source_value: number;
  status: string;
  approved_by: string | null;
  approved_at: string | null;
  applied_at: string | null;
  note: string | null;
  created_at: string | null;
}

export async function getLatestDqRun(): Promise<DqRunFull> {
  const { data } = await api.get<DqRunFull>("/api/admin/data-quality/latest");
  return data;
}

export async function listDqRuns(): Promise<DqRun[]> {
  const { data } = await api.get<DqRun[]>("/api/admin/data-quality/runs");
  return data;
}

export async function triggerDqCheck(): Promise<void> {
  await api.post("/api/admin/data-quality/trigger");
}

export async function approveDqProposal(id: number, note?: string): Promise<void> {
  await api.post(`/api/admin/data-quality/proposals/${id}/approve`, { note: note ?? null });
}

export async function rejectDqProposal(id: number, note?: string): Promise<void> {
  await api.post(`/api/admin/data-quality/proposals/${id}/reject`, { note: note ?? null });
}

// ── Changelog ("Was ist neu") ──────────────────────────────────────────────

export type ChangelogCategory = "feature" | "berechnung" | "fehlerbehebung";

export type ChangelogStatus = "draft" | "published";

export interface ChangelogEntry {
  id: number;
  entry_date: string;
  category: ChangelogCategory;
  title: string;
  description: string;
  author: string;
  status: ChangelogStatus;
  published_at: string | null;
  published_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface ChangelogListResponse {
  entries: ChangelogEntry[];
  total: number;
}

export interface ChangelogEntryBody {
  entry_date: string;
  category: ChangelogCategory;
  title: string;
  description: string;
}

export async function listChangelogEntries(limit = 20, offset = 0): Promise<ChangelogListResponse> {
  const { data } = await api.get<ChangelogListResponse>("/api/changelog", { params: { limit, offset } });
  return data;
}

export async function getChangelogUnreadCount(): Promise<number> {
  try {
    const { data } = await api.get<{ count: number }>(
      "/api/changelog/unread-count",
      { skipErrorToast: true } as object,
    );
    return data?.count ?? 0;
  } catch {
    return 0;
  }
}

export async function markChangelogSeen(): Promise<void> {
  await api.post("/api/changelog/mark-seen");
}

export async function createChangelogEntry(body: ChangelogEntryBody): Promise<{ id: number }> {
  const { data } = await api.post<{ id: number }>("/api/changelog", body);
  return data;
}

export async function updateChangelogEntry(id: number, body: ChangelogEntryBody): Promise<void> {
  await api.patch(`/api/changelog/${id}`, body);
}

export async function deleteChangelogEntry(id: number): Promise<void> {
  await api.delete(`/api/changelog/${id}`);
}

export async function listChangelogDrafts(options?: ApiRequestOptions): Promise<ChangelogListResponse> {
  const { data } = await api.get<ChangelogListResponse>("/api/changelog/drafts", options);
  return data;
}

export async function publishChangelogEntry(id: number): Promise<void> {
  await api.post(`/api/changelog/${id}/publish`);
}

export interface AltaProduct {
  id: number;
  name: string;
  short_desc: string;
  expert_arg: string;
  link: string | null;
  kpi_codes: string[];
  active: boolean;
  created_at: string;
  updated_at: string;
}
export interface AutopilotSlide {
  kpi: string;
  label: string;
  unit: string;
  has_data: boolean;
  value_fmt: string;
  status: "green" | "yellow" | "red" | "grey";
  lower_is_better: boolean;
  bench_avg_fmt: string;
  bench_gap_fmt: string;
  trend_direction: "up" | "down" | "flat";
  trend_fmt: string;
  discussed_last_time?: boolean;
  product_recommendation?: ProductRecommendation | null;
}

export interface AutopilotReviewItem {
  kpi: string;
  label: string;
  unit: string;
  prev_fmt: string;
  curr_fmt: string;
  delta_fmt: string;
  verdict: "improved" | "unchanged" | "worse";
  was_discussed: boolean;
  lower_is_better: boolean;
}

export interface AutopilotReview {
  prev_session_id: number;
  prev_date: string | null;
  prev_month: string | null;
  items: AutopilotReviewItem[];
  done_notes: { id: number; note_text: string }[];
  still_open_count: number;
}

export interface AutopilotScript {
  herd_id: string;
  herd_name: string;
  section: string;
  section_label: string;
  latest_month: string | null;
  review: AutopilotReview | null;
  slides: AutopilotSlide[];
}

export interface AutopilotCommentary {
  herd_id: string;
  kpi: string;
  einordnung: string;
  verified: boolean;
  hints: { doc_name: string; text: string; kpi?: string }[];
  cached: boolean;
  diskussionsfragen?: string[];
}

export async function getAutopilotScript(
  herdId: string,
  section: string,
): Promise<AutopilotScript> {
  const { data } = await api.get<AutopilotScript>(
    `/api/herds/${herdId}/autopilot/${section}`,
  );
  return data;
}

export async function getAutopilotCommentary(
  herdId: string,
  section: string,
  kpi: string,
  lang?: string,
): Promise<AutopilotCommentary> {
  const { data } = await api.get<AutopilotCommentary>(
    `/api/herds/${herdId}/autopilot/${section}/commentary`,
    { params: { kpi, ...(lang ? { lang } : {}) } },
  );
  return data;
}

export async function listActiveProducts(): Promise<AltaProduct[]> {
  const { data } = await api.get<AltaProduct[]>("/api/products/active");
  return data;
}

export async function listAdminProducts(): Promise<AltaProduct[]> {
  const { data } = await api.get<AltaProduct[]>("/api/admin/products");
  return data;
}

export async function createAdminProduct(body: {
  name: string;
  short_desc: string;
  expert_arg: string;
  link?: string | null;
  kpi_codes?: string[];
  active?: boolean;
}): Promise<AltaProduct> {
  const { data } = await api.post<AltaProduct>("/api/admin/products", body);
  return data;
}

export interface ProductRecommendation {
  id: number;
  name: string;
  short_desc: string;
  expert_arg: string;
  link: string | null;
}

export async function updateAdminProduct(
  id: number,
  body: {
    name: string;
    short_desc: string;
    expert_arg: string;
    link?: string | null;
    kpi_codes?: string[];
    active?: boolean;
  },
): Promise<AltaProduct> {
  const { data } = await api.put<AltaProduct>(`/api/admin/products/${id}`, body);
  return data;
}

export async function deleteAdminProduct(id: number): Promise<void> {
  await api.delete(`/api/admin/products/${id}`);
}

export interface DemoRedeemResponse {
  role: string;
  demo_link_id: number;
  language: string;
  expires_at: string | null;
}

export interface DemoLead {
  id: number;
  demo_link_id: number;
  email: string;
  farm_size: string | null;
  interest_areas: string | null;
  current_situation: string | null;
  assigned_rep: string | null;
  status: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
  link_label: string | null;
  created_by_username: string;
  link_last_seen: string | null;
  link_views: number;
}

export async function createDemoLink(body: {
  label?: string;
  expected_email: string;
  duration_days?: number;
  language?: string;
}): Promise<{ id: number; token: string; expires_at: string; language: string }> {
  const { data } = await api.post<{ id: number; token: string; expires_at: string; language: string }>(
    "/api/demo/links",
    body,
  );
  return data;
}

export async function updateDemoLead(
  id: number,
  body: { status?: string; notes?: string },
): Promise<{ ok: boolean }> {
  const { data } = await api.patch<{ ok: boolean }>(`/api/demo/leads/${id}`, body);
  return data;
}

/** Public — no auth token needed */
export async function redeemDemoLink(token: string, email: string): Promise<DemoRedeemResponse> {
  const { data } = await api.post<DemoRedeemResponse>(`/api/demo/link/${token}/redeem`, { email });
  return data;
}

export interface DemoPublicInfo {
  status: "active" | "redeemed" | "expired" | "revoked";
  label: string | null;
  language: string;
  expires_at: string | null;
  redeemed: boolean;
}

export async function submitDemoLead(body: {
  farm_size?: string;
  interest_areas?: string[];
  current_situation?: string;
  assigned_rep?: string;
}): Promise<{ ok: boolean; lead_id: number }> {
  const { data } = await api.post<{ ok: boolean; lead_id: number }>("/api/demo/lead", body);
  return data;
}

export async function logDemoEvent(eventType: string, details?: Record<string, unknown>): Promise<void> {
  await api.post("/api/demo/event", { event_type: eventType, details }).catch(() => {});
}

/** Public — no auth token needed */
export async function getDemoLinkPublicInfo(token: string): Promise<DemoPublicInfo> {
  const { data } = await api.get<DemoPublicInfo>(`/api/demo/link/${token}`);
  return data;
}

export async function listDemoLinks(): Promise<DemoLinkInfo[]> {
  const { data } = await api.get<DemoLinkInfo[]>("/api/demo/links");
  return data;
}

export async function revokeDemoLink(id: number): Promise<void> {
  await api.delete(`/api/demo/links/${id}`);
}

// Guest visits (task 1556).  These endpoints intentionally use a separate
// namespace from demo links and normal invitations.
export interface GuestVisitRecipient {
  id: number;
  email: string;
  status: string;
  mail_status?: string;
  last_access_at?: string | null;
  revoked_at?: string | null;
  expires_at?: string;
}
export interface GuestVisit {
  id: number;
  label: string | null;
  herd_id: string;
  herd_name?: string | null;
  language: string;
  expires_at: string;
  status: string;
  recipients: GuestVisitRecipient[];
}
export interface GuestPublicInfo {
  status: string;
  language: string;
  expires_at?: string | null;
  recipient_hint?: string | null;
}

export async function listGuestVisits(): Promise<GuestVisit[]> {
  const { data } = await api.get<GuestVisit[]>("/api/guest-visits");
  return data;
}
export async function createGuestVisit(body: {
  label?: string;
  herd_id: string;
  language: string;
  expires_at: string;
  recipients: string[];
}): Promise<GuestVisit> {
  const { data } = await api.post<GuestVisit>("/api/guest-visits", body);
  return data;
}
export async function revokeGuestVisit(id: number, reason = ""): Promise<void> {
  await api.post(`/api/guest-visits/${id}/revoke`, { reason });
}
export async function revokeGuestRecipient(visitId: number, recipientId: number, reason = ""): Promise<void> {
  await api.post(`/api/guest-visits/${visitId}/recipients/${recipientId}/revoke`, { reason });
}
export async function resendGuestRecipient(visitId: number, recipientId: number): Promise<void> {
  await api.post(`/api/guest-visits/${visitId}/recipients/${recipientId}/resend`);
}
export async function getGuestPublicInfo(token: string): Promise<GuestPublicInfo> {
  const { data } = await api.get<GuestPublicInfo>(`/api/guest/${encodeURIComponent(token)}`, {
    skipErrorToast: true,
  } as object);
  return data;
}
export async function setGuestPreAuthLanguage(token: string, language: string): Promise<void> {
  await api.put(`/api/guest/link/${encodeURIComponent(token)}/language`, { language }, { skipErrorToast: true } as object);
}
export async function getGuestMe(): Promise<{ role: "guest"; herd_id: string; label: string; expires_at: string | null; language: string }> {
  const { data } = await api.get("/api/guest/me");
  return data;
}
export interface GuestLimits { min_days: number; max_days: number; max_recipients: number; otp_ttl_seconds: number }
export async function getGuestLimits(): Promise<GuestLimits> {
  const { data } = await api.get<GuestLimits>("/api/guest/limits", { skipErrorToast: true } as object);
  return data;
}
export async function suspendGuestVisit(id: number, reason = ""): Promise<void> {
  await api.post(`/api/guest-visits/${id}/suspend`, { reason });
}
export async function resumeGuestVisit(id: number, reason = ""): Promise<void> {
  await api.post(`/api/guest-visits/${id}/resume`, { reason });
}
export async function extendGuestVisit(id: number, expires_at: string, reason = ""): Promise<void> {
  await api.post(`/api/guest-visits/${id}/extend`, { expires_at, reason });
}
export async function requestGuestOtp(token: string, language?: string): Promise<{ ok: boolean; retry_after?: number }> {
  const { data } = await api.post<{ ok: boolean; retry_after?: number }>(
    `/api/guest/${encodeURIComponent(token)}/request-code`, { language },
  );
  return data;
}
export async function verifyGuestOtp(token: string, code: string): Promise<{ role: "guest"; expires_at: string; herd_id: string }> {
  const { data } = await api.post<{ role: "guest"; expires_at: string; herd_id: string }>(
    `/api/guest/${encodeURIComponent(token)}/verify`, { code },
  );
  return data;
}
export async function setGuestLanguage(language: string): Promise<void> {
  await api.put("/api/guest/preferences/language", { language });
}

export async function listDemoLeads(): Promise<DemoLead[]> {
  const { data } = await api.get<DemoLead[]>("/api/demo/leads");
  return data;
}

export async function getSyncSidebarStatus(): Promise<SyncSidebarStatusResponse> {
  return shortSessionStatusRead("sync-sidebar", async () => {
    const { data } = await api.get<SyncSidebarStatusResponse>("/api/sync/sidebar-status", {
      skipErrorToast: true,
    } as object);
    return data;
  });
}

export interface BenchmarkSurfaceScope {
  kind: "single" | "consolidated";
  herd_ids?: string[];
}

export interface BenchmarkSurfaceResponse {
  herd_id: string;
  month: string | null;
  reference_selection: {
    requested_month: string;
    resolved_month: string | null;
    status: string;
    reason: string | null;
  };
  sync_date: string;
  diagnosis_cutoff: string;
  mode: BenchmarkSurfaceMode;
  value: null | "0-400" | "400-600" | "600-1000" | ">1000" | number;
  cohort_version: string;
  data_version: string;
  nominal_peer_count: number;
  selection_status: BenchmarkSelectionStatus;
  table: BenchmarkSurfaceTableRow[];
  bubble: {
    top10_threshold: number | null;
    lower_is_better: boolean;
    own_point: BenchmarkSurfaceBubblePoint | null;
    peer_points: BenchmarkSurfaceBubblePoint[];
  };
}

export async function queryBenchmarkSurface(
  herdId: string,
  body: {
    scope: BenchmarkSurfaceScope;
    mode: BenchmarkSurfaceMode;
    value: BenchmarkSurfaceResponse["value"];
    month: string;
    period?: "monthly" | "quarterly";
    reference_policy?: "exact" | "latest_available_closed";
    table_kpis: string[];
    bubble_axes: { x_kpi: string; y_kpi: string; size_kpi: string };
  },
  requestOptions?: ApiRequestOptions,
): Promise<BenchmarkSurfaceResponse> {
  const { data } = await api.post<BenchmarkSurfaceResponse>(
    `/api/herds/${herdId}/benchmark/surface-query`, body, requestOptions as object,
  );
  assertBenchmarkSelectionContract(data);
  return data;
}

export async function querySnapshotBenchmarkSurface(
  rootHash: string,
  herdId: string,
  body: Parameters<typeof queryBenchmarkSurface>[1],
  overlayHash?: string,
  requestOptions?: ApiRequestOptions,
): Promise<BenchmarkSurfaceResponse> {
  const quietOptions = {
    ...requestOptions,
    // Overview/Analyse render their own localized retry state. A parallel
    // benchmark failure must not add one global toast per projection request.
    skipErrorToast: requestOptions?.skipErrorToast ?? true,
  };
  const { data } = await api.post<BenchmarkSurfaceResponse>(
    `/api/snapshots/${rootHash}/${herdId}/benchmark/surface`,
    body,
    {
      params: { overlay: overlayHash },
      ...snapshotOptions(quietOptions, {
        root: rootHash,
        overlay: overlayHash,
        herd: herdId,
        scope: `${body.scope.kind}:${(body.scope.herd_ids ?? []).join(",")}`,
        mode: body.mode,
        period: body.period ?? "monthly",
        requestedMonth: body.month,
        referencePolicy: body.reference_policy ?? "exact",
        value: body.value,
        axes: body.bubble_axes,
        tableKpis: body.table_kpis,
      }, "snapshot-benchmark-surface"),
    } as object,
  );
  assertBenchmarkSelectionContract(data);
  return data;
}

export interface BenchmarkSurfaceTableRow {
  kpi_key: string;
  own_value: number | null;
  rank: number | null;
  total_herds: number;
  average: number | null;
  top10: number | null;
  p25?: number | null;
  top_farm?: number | null;
  score: number | null;
  maturity_status?: "mature" | "immature";
  unavailable_reason?: "diagnosis_incomplete" | "incomplete_period" | null;
}

export interface BenchmarkBandScope {
  kind: "single" | "consolidated";
  herd_ids?: string[];
}

export interface BenchmarkBandPoint {
  period: string;
  requested_period: string;
  /** Effective source months used for a running-quarter point, when exposed
   * by the pinned adapter. Complete quarters contain all three months. */
  effective_months?: string[];
  aligned_fallback?: boolean;
  average: number | null;
  p10: number | null;
  p25: number | null;
  p50: number | null;
  p75: number | null;
  p90: number | null;
  top_farm: number | null;
  n_herds: number;
  band_status: "insufficient" | "inner_only" | "full";
  preliminary: boolean;
  maturity_status: "mature" | "preliminary" | "immature" | "unsupported";
  unavailable_reason: BenchmarkPointReason | null;
}

export type BenchmarkPointReason =
  | "diagnosis_incomplete" | "immature_period" | "source_incomplete"
  | "unsupported_aggregation" | "incomplete_period" | "unsupported_period"
  | "insufficient_peers" | "no_values";

export interface BenchmarkSurfaceBubblePoint {
  x_val: number | null;
  y_val: number | null;
  size_val: number | null;
  /** Never present for anonymized peer points. */
  herd_id?: string;
}

export type BenchmarkSurfaceMode = "all" | "size_class" | "peer_group";

export interface BenchmarkBandSelection {
  chart_id: string;
  kpi_key: string;
  mode: BenchmarkSelectionMode;
  value: BenchmarkSelectionValue;
}

export type BenchmarkSelectionValue = null | "0-400" | "400-600" | "600-1000" | ">1000" | "2x" | "3x" | "robot" | number;

export interface BenchmarkBandsQueryResponse {
  herd_id: string;
  period: "monthly" | "quarterly";
  requested_months: string[];
  /** Immutable root watermark and the canonical 60-day diagnosis boundary. */
  sync_date: string;
  diagnosis_cutoff: string;
  selections: BenchmarkBandSelectionResult[];
  remontierung_contract?: AnalyseResponse["remontierung_contract"];
}

export interface BenchmarkBandSelectionResult extends BenchmarkBandSelection {
  cohort_version: string;
  data_version: string;
  as_of: string;
  stale_as_of: boolean;
  nominal_peer_count: number;
  selection_status: BenchmarkSelectionStatus;
  points: BenchmarkBandPoint[];
  /** Scalar comparison used by scorecard tiles/compass.  Trend points remain canonical. */
  comparison?: BenchmarkComparisonSummary;
  remontierung_contract?: AnalyseResponse["remontierung_contract"];
}

export type BenchmarkComparisonMaturity = "mature" | "preliminary";
export type BenchmarkComparisonReason =
  | "diagnosis_incomplete" | "immature_period" | "source_incomplete"
  | "unsupported_aggregation" | "incomplete_period" | "no_values"
  | "insufficient_peers" | "unsupported_period" | "partial_coverage"
  | "technical_error" | "no_reference";

export interface BenchmarkComparisonSummary {
  requested_period: string;
  reference_period: string | null;
  maturity: BenchmarkComparisonMaturity;
  status: BenchmarkSelectionStatus;
  reason: BenchmarkComparisonReason | null;
  n: number;
  average: number | null;
  median: number | null;
  p25: number | null;
  top10: number | null;
  top_farm: number | null;
  reporting_herd_count: number;
  selected_herd_count: number;
  effective_months: string[];
}

export type BenchmarkSelectionStatus =
  | "ok" | "partial" | "empty_cohort" | "no_valid_values" | "unsupported_period" | "unavailable";

/** A successful HTTP response without the versioned selection contract is unsafe to render. */
export class BenchmarkContractError extends Error {
  constructor() {
    super("Invalid benchmark selection response");
    this.name = "BenchmarkContractError";
  }
}

const benchmarkStatuses: readonly BenchmarkSelectionStatus[] = [
  "ok", "partial", "empty_cohort", "no_valid_values", "unsupported_period", "unavailable",
];
const comparisonReasons: readonly BenchmarkComparisonReason[] = [
  "diagnosis_incomplete", "immature_period", "source_incomplete",
  "unsupported_aggregation", "incomplete_period", "no_values", "insufficient_peers",
  "unsupported_period", "partial_coverage", "technical_error", "no_reference",
];
const pointReasons: readonly BenchmarkPointReason[] = [
  "diagnosis_incomplete", "immature_period", "source_incomplete",
  "unsupported_aggregation", "incomplete_period", "unsupported_period",
  "insufficient_peers", "no_values",
];
const finiteOrNull = (v: unknown): v is number | null =>
  v === null || (typeof v === "number" && Number.isFinite(v));

function assertComparisonSummary(value: unknown): asserts value is BenchmarkComparisonSummary {
  const summary = value as Record<string, unknown> | null;
  if (!summary || typeof summary.requested_period !== "string"
    || (summary.reference_period !== null && typeof summary.reference_period !== "string")
    || !["mature", "preliminary"].includes(String(summary.maturity))
    || !benchmarkStatuses.includes(summary.status as BenchmarkSelectionStatus)
    || (summary.reason !== null && !comparisonReasons.includes(summary.reason as BenchmarkComparisonReason))
    || !Number.isInteger(summary.n) || Number(summary.n) < 0
    || !finiteOrNull(summary.average) || !finiteOrNull(summary.median)
    || !finiteOrNull(summary.p25) || !finiteOrNull(summary.top10)
    || !finiteOrNull(summary.top_farm)
    || !Number.isInteger(summary.reporting_herd_count) || Number(summary.reporting_herd_count) < 0
    || !Number.isInteger(summary.selected_herd_count) || Number(summary.selected_herd_count) < 0
    || !Array.isArray(summary.effective_months)
    || !summary.effective_months.every((month) => typeof month === "string")) {
    throw new BenchmarkContractError();
  }
}

/** Validate a bands response atomically; no partially malformed batch is renderable. */
export function assertBenchmarkBandsContract(
  value: unknown,
  expected?: {
    herdId: string;
    selections: readonly BenchmarkBandSelection[];
    period: "monthly" | "quarterly";
    months: readonly string[];
  },
): asserts value is BenchmarkBandsQueryResponse {
  const response = value as Record<string, unknown> | null;
  if (!response || typeof response.herd_id !== "string"
    || !["monthly", "quarterly"].includes(String(response.period))
    || typeof response.sync_date !== "string"
    || typeof response.diagnosis_cutoff !== "string"
    || !Array.isArray(response.requested_months) || !response.requested_months.every((m) => typeof m === "string")
    || !Array.isArray(response.selections)) throw new BenchmarkContractError();
  const selections = response.selections as unknown[];
  if (expected && (response.herd_id !== expected.herdId
    || response.period !== expected.period
    || JSON.stringify(response.requested_months) !== JSON.stringify(expected.months)
    || selections.length !== expected.selections.length
    || selections.some((item, index) => {
      const result = item as Record<string, unknown> | null;
      const expectedSelection = expected.selections[index];
      return !result || result.chart_id !== expectedSelection.chart_id
        || result.kpi_key !== expectedSelection.kpi_key
        || result.mode !== expectedSelection.mode || result.value !== expectedSelection.value;
    }))) throw new BenchmarkContractError();
  selections.forEach((item) => {
    assertBenchmarkSelectionContract(item);
    const result = item as BenchmarkBandSelectionResult;
    if (!Array.isArray(result.points) || result.comparison === undefined) {
      throw new BenchmarkContractError();
    }
    result.points.forEach((point) => {
      if (typeof point.period !== "string" || point.requested_period !== point.period
        || typeof point.preliminary !== "boolean"
        || (point.aligned_fallback !== undefined && typeof point.aligned_fallback !== "boolean")
        || !["mature", "preliminary", "immature", "unsupported"].includes(point.maturity_status)
        || (point.unavailable_reason !== null && !pointReasons.includes(point.unavailable_reason))
        || !Number.isInteger(point.n_herds) || point.n_herds < 0
        || !["insufficient", "inner_only", "full"].includes(point.band_status)
        || ![point.average, point.p10, point.p25, point.p50, point.p75,
          point.p90, point.top_farm].every(finiteOrNull)) {
        throw new BenchmarkContractError();
      }
    });
    assertComparisonSummary(result.comparison);
  });
  const requestedMonths = response.requested_months as string[];
  const expectedPeriods = response.period === "quarterly"
    ? requestedMonths.filter((_, index) => index % 3 === 0).map((month) =>
        `${month.slice(0, 4)}-Q${Math.floor((Number(month.slice(5)) - 1) / 3) + 1}`)
    : requestedMonths;
  if (selections.some((item) => {
    const points = (item as BenchmarkBandSelectionResult).points;
    return points.length !== expectedPeriods.length
      || points.some((point, index) => point.period !== expectedPeriods[index]);
  })) throw new BenchmarkContractError();
}

export function assertBenchmarkSelectionContract(value: unknown): asserts value is {
  nominal_peer_count: number;
  selection_status: BenchmarkSelectionStatus;
  cohort_version: string;
} {
  const response = value as Record<string, unknown> | null;
  if (!response
    || !Number.isInteger(response.nominal_peer_count)
    || (response.nominal_peer_count as number) < 0
    || typeof response.cohort_version !== "string"
    || !response.cohort_version
    || !benchmarkStatuses.includes(response.selection_status as BenchmarkSelectionStatus)) {
    throw new BenchmarkContractError();
  }
}

export async function queryBenchmarkBands(
  herdId: string,
  body: {
    selections: BenchmarkBandSelection[];
    period: "monthly" | "quarterly";
    months: string[];
    scope: BenchmarkBandScope;
  },
  requestOptions?: ApiRequestOptions,
): Promise<BenchmarkBandsQueryResponse> {
  const { data } = await api.post<BenchmarkBandsQueryResponse>(
    `/api/herds/${herdId}/benchmark-bands/query`,
    body,
    requestOptions as object,
  );
  assertBenchmarkBandsContract(data, {
    herdId, selections: body.selections, period: body.period, months: body.months,
  });
  return data;
}

export async function querySnapshotBenchmarkBands(
  rootHash: string,
  herdId: string,
  body: Parameters<typeof queryBenchmarkBands>[1],
  overlayHash?: string,
  requestOptions?: ApiRequestOptions,
): Promise<BenchmarkBandsQueryResponse> {
  const quietOptions = {
    ...requestOptions,
    // These deferred chart requests have an inline retry state; suppress the
    // redundant global toast, especially when several projections fail at once.
    skipErrorToast: requestOptions?.skipErrorToast ?? true,
  };
  const { data } = await api.post<BenchmarkBandsQueryResponse>(
    `/api/snapshots/${rootHash}/${herdId}/benchmark/bands`,
    body,
    {
      params: { overlay: overlayHash },
      ...snapshotOptions(quietOptions, {
        root: rootHash,
        overlay: overlayHash,
        herd: herdId,
        scope: `${body.scope.kind}:${(body.scope.herd_ids ?? []).join(",")}`,
        mode: body.period,
        period: body.period,
        months: body.months,
        selections: body.selections,
      }, "snapshot-benchmark-bands"),
    } as object,
  );
  assertBenchmarkBandsContract(data, {
    herdId, selections: body.selections, period: body.period, months: body.months,
  });
  return data;
}
